import { amorphicStatic } from '@haventech/amorphic';
import { WholeLifeValidationUtils } from '@hit/validation-utils';
import { Assumptions } from '../../../common/js/static/Assumptions';
import { RateClasses } from '../../../common/js/static/RateClasses';
import {
  CPQuoteRateClassDescriptions,
  Exchange1035PolicyType,
  FeatureFlags,
  Exchange1035FundingPurpose,
  RateClassDescriptionType,
  AnnuityInflationProtector,
  AnnuityApplicationMetadataFileName,
  AnnuitySuitabilityMetadataFileName,
  AnnuityReplacementMetadataFileName,
  AnnuityCertificateOfTrustMetadataFileName,
  AnnuityFR3013MetadataFileName,
} from '../interfaces/CoverpathTypes';
import { Policy } from '../../../common/js/customer/Policy';
import { Application } from '../../../common/js/customer/Application';
import { Customer } from '../../../common/js/customer/Customer';
import { MetadataHierarchy } from '../../../common/js/metadata/MetadataHierarchy';
import { PolicyAdminStatus } from '../../../common/js/policyAdmin/PolicyAdminStatus';
import { PremiumDisplay } from '../../angular/interfaces/Type';
import { LaunchDarklyInstance, LD_APP } from '../../../common/js/launch-darkly/LaunchDarklySingleton';
import { ExistingPolicy } from '../../../common/js/customer/ExistingPolicy';
import { Exchange1035 } from '../../../common/js/quote/coverpath/Exchange1035';
import { LowFaceWholeLife, Product, productTypes, RiderType, TimeFrequency } from '../../../common/js/static/Product';
import { Utils } from '../../../common/js/Utils';
import { CommonUtils } from '../../../common/js/static/CommonUtils';
import { AccountCenterListRecordView, ClientPolicyStatus } from '../../../coverpath-agent/public/js/types/Types';
import moment from 'moment';
import { DateTime } from 'luxon';
import _ from 'lodash';
import jwt from 'jsonwebtoken';
import { Person } from '../../../common/js/customer/Person';
import { PolicyAmendmentProperty } from '../../../common/js/static/PolicyAmendmentProperty';
import { POLICY_CHANGE_HISTORY_STATUS } from '../../../common/js/static/PolicyChangeHistoryStatus';
import { CollectionItemChange, PolicyChangeHistory } from '../../../common/js/customer/PolicyChangeHistory';
import { CoverpathPolicy } from '../../classes/coverpath_subclasses/CoverpathPolicy';
import { AgencyCorpsRelationship } from '../../../common/js/services/CdmService';
import { StateKeyValuePairs } from '../../../common/js/interfaces/CommonTypes';
import { PolicyRider } from '../../../common/js/customer/PolicyRider';
import { CoverpathBasePolicy } from '../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { CoverpathCustomer } from '../../classes/coverpath_subclasses/CoverpathCustomer';
import { CasApplication } from '../../classes/coverpath_subclasses/CasApplication';
import { FlatExtraMasks, FlatExtraNames } from '../../../common/js/lib/FlatExtraNames';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';
import { FlatExtra } from '../../../common/js/customer/FlatExtra';
import { FlatExtra as uwFlatExtra } from '@hit/underwriting-api-client';

export enum ReasonForOR1 {
  USE_CASE_CRUSHER,
  CP_JOINT_OWNERSHIP,
  CP_CASE,
  UNKNOWN,
}

declare const window: any;

const SWAP_FIELDS_FROM_AMENDMENT = [
  'effectiveDate',
  'tLICEffectiveDate',
  'faceAmount',
  'maxFace',
  'maxAnnualPremium',
  'paymentFrequency',
  'hasEcp',
  'product',
  'productType',
  'selectedQuote',
  'term',
  'waiver',
  'wholeAPL',
  'loanInterestType',
  'dividendOption',
  'premiumBreakdown.base',
  'premiumBreakdown.waiver',
  'tLICPremiumBreakdown.base',
  'tLICPremiumBreakdown.waiver',
  'initialAcceleratedRateClass',
  'acceleratedRateClass',
  'tLICRateClass',
  'tLICAlgoRateClass',
  'finalAlgoRateClass',
  'finalManualRateClass',
  'finalRateClass',
  'type',
];

export class CoverpathUtils {
  static readonly deprecatedMetadata2016Version = 14;
  //TODO: HL-17298 set this after merging down the feature branch
  static readonly deprecatedICCMetadata2018Version = 55;
  static readonly deprecatedNonICCMetadata2018Version = 66;

  //TODO: HL-26991 Need to confirm version numbers for the following
  static readonly deprecatedMetadata2019Version = 88;
  static readonly deprecatedMetadata2020Version = 132;

  static readonly metadataVersionWithExtraIDQuestions = 100;

  static readonly teleCMIMetadataVersion = 165;

  static readonly EXAM_PREFIX_SUMMARY_STATUS = 'Exam:';

  static readonly UNDERWRITING_LITETOUCH_STATUS_TEXT: string = 'Underwriting: LiteTouch';
  static readonly UNDERWRITING_STATUS_TEXT: string = 'Underwriting';

  static readonly UNDERWRITING_LITETOUCH_TOOTIP_TEXT =
    'This case is undergoing a second review by an underwriter to see if it can be approved without a medical exam. Typically the second review is completed within 48 hours.';

  static readonly defaultTaxBracket = 32;

  static readonly SANDERLING_CUSTOMER_SUPPORT_PHONE = '1-866-957-5347';

  static readonly TELE_CMI_TEAM_PHONE = '1-844-835-3264';

  /**
   * Check if a feature is enabled sitewide
   *
   * @remarks
   * In order for a feature to be enabled sitewide we need to define its key-value pair in the environment config files under the features section.
   *
   * @param {string} feature - Name of the feature to check for.
   * @returns {boolean} If the feature has been defined and if it's value is set to true.
   */
  static isFeatureEnabled(feature: string): boolean {
    const config = amorphicStatic.config.nconf.get('features');
    const featureValue = config[feature];
    return featureValue === true || featureValue === 'true';
  }

  static async excludeJointFromOR1(userId: string) {
    let excludeJointFromOR1: boolean = false;
    const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);
    if (ldInstance) {
      excludeJointFromOR1 = await ldInstance.getFeatureFlagValueAsBoolean(FeatureFlags.excludeJointFromOR1, userId);
    }
    return excludeJointFromOR1;
  }

  static scrollToElement(id: string): void {
    const element = document.getElementById(id);
    element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  static scrollToTop(isSmooth?: boolean): void {
    if (isSmooth) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    } else {
      window.scrollTo(0, 0);
    }
  }

  static doesQuickQuoteExist(): boolean {
    const element = document.getElementById('quick-quote');

    return !!element;
  }

  static showLoadingPage() {
    document.getElementById('loading-screen').style.opacity = '1';
  }

  static hideLoadingPage() {
    if (document.getElementById('loading-screen')) {
      document.getElementById('loading-screen').style.opacity = '0';
    }
  }

  static dataLayerPush(data: object): void {
    window.dataLayer.push(data);
  }

  static makePaymentFrequencySafeForPolicyStrict(input: string): 'annual' | 'monthly' {
    const possiblyFalsey = CommonUtils.makePaymentFrequencySafeForPolicy(input);

    if (!!possiblyFalsey) {
      return possiblyFalsey as 'annual' | 'monthly';
    }

    throw Error(`Can't make ${input} safe for a paymentFrequency on a policy`);
  }

  /**
   * Returns a string with paymentFrequency convention expected by the Product Service (e.g., Monthly, Annual)
   */
  static makePaymentFrequencySafeForProductService(input: string): string {
    //Coverpath Quote convention === Product Service convention
    return CommonUtils.makePaymentFrequencySafeForCoverpathQuote(input);
  }

  /**
   * Returns payment frequencey value that will be displayed under the premium in the quote page.
   *
   * @param {string} input - The current value of the radio buttons for the payment frequency in the quote page.
   * @returns {string} - frequency that should be shown under premium ( per year or per month ).
   */
  static makePaymentFrequencyForQuoteDisplay(input: string): string {
    switch (input.toLowerCase()) {
      case 'monthly':
        return 'month';
      case 'annual':
        return 'year';
      default:
        throw Error(`Invalid payment frequency.`);
    }
  }

  static getJointAppSupportedStateValues(): StateKeyValuePairs {
    const wholeStates = CommonUtils.getSupportedStateValues(Assumptions.coverpathUnsupportedStatesWhole);
    const termStates = CommonUtils.getSupportedStateValues(Assumptions.coverpathUnsupportedStatesTerm);

    const wholeCodeList = Object.keys(wholeStates);
    const termCodeList = Object.keys(termStates);
    const jointAppCodeList = wholeCodeList.filter((code) => termCodeList.includes(code));

    const supportedStates = {};
    jointAppCodeList.forEach((stateCode) => {
      supportedStates[stateCode] = termStates[stateCode];
    });
    return supportedStates;
  }

  static getAllSupportedStateValues(): StateKeyValuePairs {
    const wholeStates = CommonUtils.getSupportedStateValues(Assumptions.coverpathUnsupportedStatesWhole);
    const termStates = CommonUtils.getSupportedStateValues(Assumptions.coverpathUnsupportedStatesTerm);

    return Object.assign({}, wholeStates, termStates);
  }

  static async getAnnuityBestInterestStates(userId: string): Promise<string[]> {
    const ld = await LaunchDarklyInstance.getInstance(LD_APP.cp);
    const ldValue = await ld.getFeatureFlagValueAsString(FeatureFlags.annuityNAICBestInterestStates, userId);
    return ldValue.split(',').map((value) => value.trim());
  }

  static async getUnsupportedStatesForAnnuity(): Promise<string[]> {
    const ld = await LaunchDarklyInstance.getInstance(LD_APP.cp);
    const ldValue = await ld.getFeatureFlagValueAsString(
      FeatureFlags.unsupportedStatesForAnnuity,
      'getUnsupportedStatesForAnnuity',
      'NY,CA,ND,SD,CT,FL,PR'
    );
    return ldValue.split(',').map((value) => value.trim());
  }
  static async isBIState(state: string): Promise<boolean> {
    const listOfBISupportedStates = await CoverpathUtils.getAnnuityBestInterestStates(state);
    return listOfBISupportedStates.includes(state);
  }

  /**
   * @param abbr ex: TN
   */
  static getStateName(abbr: string): string {
    return Assumptions.stateValues[abbr];
  }

  /**
   * returns whether a state requires gender to be displayed as Unisex
   * based on the values in AvailableStateRegistry.ts
   *
   * a context free version of the function doesPolicyRequireUnisexRates in Policy
   */
  static isUnisexState(channel: string, state: string): boolean {
    return !!MetadataHierarchy.getConfigurationByChannelAndState(channel, state, 'hasUnisexRates');
  }

  /**
   * For a given health class number (as seen in ClientBaseQuote's healthClassNumber),
   * returns its rate class string code
   * { "0":"excellent health","1":"very good health","2":"good health","3":"average health"}
   */
  static getRateClassStringCode(healthClassNumber: number, isSmoker: boolean): string {
    switch (healthClassNumber) {
      case 0:
        return isSmoker ? RateClasses.SELECT_TOBACCO.stringCode : RateClasses.ULTRA.stringCode;
      case 1:
        return isSmoker ? RateClasses.SELECT_TOBACCO.stringCode : RateClasses.SELECT.stringCode;
      case 2:
        return isSmoker ? RateClasses.SELECT_TOBACCO.stringCode : RateClasses.STANDARD.stringCode;
      case 3:
        return isSmoker ? RateClasses.STANDARD_TOBACCO.stringCode : RateClasses.STANDARD.stringCode;
      default:
        return isSmoker ? RateClasses.SELECT_TOBACCO.stringCode : RateClasses.ULTRA.stringCode;
    }
  }

  /**
   * For a given description type (i.e., longDescription, shortDescription, tobacco as defined in RateClassDescriptionType),
   * returns its corresponding descriptions used by different components of Coverpath
   */
  static getRateClassDescriptions(descriptionType: keyof RateClassDescriptionType): object {
    return {
      [RateClasses.ULTRA.stringCode]: CPQuoteRateClassDescriptions[RateClasses.ULTRA.stringCode][descriptionType],
      [RateClasses.SELECT.stringCode]: CPQuoteRateClassDescriptions[RateClasses.SELECT.stringCode][descriptionType],
      [RateClasses.STANDARD.stringCode]: CPQuoteRateClassDescriptions[RateClasses.STANDARD.stringCode][descriptionType],
      [RateClasses.SELECT_TOBACCO.stringCode]:
        CPQuoteRateClassDescriptions[RateClasses.SELECT_TOBACCO.stringCode][descriptionType],
      [RateClasses.STANDARD_TOBACCO.stringCode]:
        CPQuoteRateClassDescriptions[RateClasses.STANDARD_TOBACCO.stringCode][descriptionType],
    };
  }

  static getCirFilingNumber(): string {
    return 'ICC20CIRCP-APP';
  }

  /**
   * Get the appropriate filing number based on metadata version and state if Non-ICC
   * @param policy The primary policy of the application
   * @returns The appropraite filing number for this application
   */
  static getFilingNumber(policy: Policy): string {
    const appMetadataVersion = policy.appMetadataVersion;
    const policyState = policy.getApplicationState();

    if (appMetadataVersion && appMetadataVersion <= CoverpathUtils.deprecatedMetadata2016Version) {
      return 'ICC16A2200-LI 1215';
    }

    // Non-ICC States for 2018 filing
    if (
      policy &&
      !policy.isICCApplication() &&
      appMetadataVersion &&
      appMetadataVersion <= CoverpathUtils.deprecatedNonICCMetadata2018Version
    ) {
      switch (policyState) {
        case 'CA':
          return 'A2200CA2018CP 0818';
        case 'DE':
          return 'A2200DE2018CP 0818';
        case 'FL':
          return 'A2200FL2018CP 062018';
        case 'ND':
          return 'A2200ND2018CP 0818';
        case 'SD':
          return 'A2200SD2018CP 0818';
      }
    }

    if (appMetadataVersion && appMetadataVersion <= CoverpathUtils.deprecatedICCMetadata2018Version) {
      return 'ICC18A2200CP 0618';
    }

    // Non-ICC States for 2019 filing
    if (
      policy &&
      !policy.isICCApplication() &&
      appMetadataVersion &&
      appMetadataVersion <= CoverpathUtils.deprecatedMetadata2019Version
    ) {
      switch (policyState) {
        case 'CA':
          return 'A2200CA2019CP';
        case 'DE':
          return 'A2200CP2019';
        case 'FL':
          return 'A2200FL19CP';
        case 'ND':
          return 'A2200CP2019ND';
        case 'SD':
          return 'A2200CP2019';
      }
    }

    if (appMetadataVersion && appMetadataVersion <= CoverpathUtils.deprecatedMetadata2019Version) {
      return 'ICC19A2200CP';
    }

    // Non-ICC States for 2020 filing
    if (
      policy &&
      !policy.isICCApplication() &&
      appMetadataVersion &&
      appMetadataVersion <= CoverpathUtils.deprecatedMetadata2020Version
    ) {
      switch (policyState) {
        case 'CA':
          return 'A2200CA19CP';
        case 'DE':
        case 'SD':
          return 'A2200CP2020';
        case 'FL':
          return 'A2200CPFL2020';
        case 'ND':
          return 'A2200CP2020ND';
        case 'NY':
          return 'A2200CP20NY';
      }
    }

    if (appMetadataVersion && appMetadataVersion <= CoverpathUtils.deprecatedMetadata2020Version) {
      return 'ICC20A2200CPv1';
    }

    // Non-ICC States for 2021 filing
    if (policy && !policy.isICCApplication()) {
      switch (policyState) {
        case 'CA':
          return 'A2200CA19CP';
        case 'DE':
        case 'SD':
          return 'A2200CP2021';
        case 'FL':
          return 'A2200CP2021FL';
        case 'ND':
          return 'A2200CP2021ND';
        case 'NY':
          return 'A2200CP20NY';
      }
    }

    // default assumption is current ICC filing
    return 'ICC20A2200CPv2';
  }

  /**
   * Get the appropriate tag lines for the Policy Details header in the app (live and rendered)
   * @param application
   * @returns `lengthText`: the first tagline that corresponds to the length of the policy
   *          `productText`: the second tagline that describes the type of policy
   */
  static getAppTaglines(application: Application): { lengthText: string; productText: string } {
    let lengthText: string, productText: string;
    const primaryTermLength: number = application.primaryPolicy.term;
    const artDescription = Product.products.CASTerm.artDescription;

    if (application.isJointApp()) {
      if (application.secondaryPolicy.isAnnuallyRenewableTerm()) {
        lengthText = `${artDescription} & Whole Life ${primaryTermLength}`;
      } else {
        const secondaryTermLength: number = application.secondaryPolicy.term;
        const secondaryTermEnd: string =
          application.secondaryPolicy.isEcpProduct() && secondaryTermLength > 10 ? 'Years ECP' : 'Year Term';

        //for joint apps, the primary will be whole and secondary term... but the UX wants to show it term first
        lengthText = `${secondaryTermLength} ${secondaryTermEnd} & Whole Life ${primaryTermLength}`;
      }

      productText = 'Policies';
    } else {
      if (application.primaryPolicy.isTermLifePolicy()) {
        if (application.primaryPolicy.isAnnuallyRenewableTerm()) {
          lengthText = 'Annually Renewable';
        } else {
          const termEnd = application.primaryPolicy.isEcpProduct() && primaryTermLength > 10 ? 'Years ECP' : 'Years';
          lengthText = `${primaryTermLength?.toString()} ${termEnd}`;
        }
        productText = 'Convertible Term Policy';
      } else {
        lengthText = `WL ${primaryTermLength}`;
        productText = 'Whole Life Policy';
      }
    }

    return { lengthText, productText };
  }

  static isPreAppChecks(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'Pre-application:Submitted':
      case 'TLIC Application:Checking ID':
      case 'TLIC Application:Checking Insured ID':
      case 'TLIC Application:Checking Owner ID':
      case 'TLIC Application:Gathering Data':
      case 'TLIC Application:Initial Underwriting':
        return true;
    }
    return false;
  }

  static isNonUniqueAction(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'Accelerated Application:Waiting for Signature':
      case 'TLIC Application:Waiting for Signature':
      case 'Haven Application:Waiting for Signature':
      case 'TLIC Application:Determine Underwriting Class':
      case 'TLIC Issued:Final Underwriting':
      case 'Manual Underwriting:Final Review':
      case 'Underwriting:Underwriting':
      case 'Application:Waiting for Issue Acceptance':
      case 'TLIC Issued:Waiting for Policy Acceptance':
        return true;
    }
    return false;
  }

  static isInitialProcessing(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'null:null':
      case 'Accelerated Application:Determine Underwriting Class':
      case 'License-verification:Fluidless Invalid':
      case 'License-verification:Invalid':
      case 'License-verification:Verifying':
      case 'License-verification:Verifying Fluidless':
      case 'Loading:loading':
      case 'Pre-application:Submitted':
      case 'TLIC Application:Checking ID':
      case 'TLIC Application:Checking Insured ID':
      case 'TLIC Application:Checking Owner ID':
      case 'TLIC Application:Correcting Insured SSN':
      case 'TLIC Application:Correcting License':
      case 'TLIC Application:Correcting Owner SSN':
      case 'TLIC Application:Correcting SSN':
      case 'TLIC Application:Gathering Data':
      case 'TLIC Application:Initial Underwriting':
      case 'Timeout:':
        return true;
    }
    return false;
  }

  static isIssuingPolicy(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'Accelerated Application:Issuing Policy':
      case 'Application:Issuing Policy':
      case 'TLIC Issued:Issuing Policy':
        return true;
    }
    return false;
  }

  static isInManualUnderwriting(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'Manual Underwriting:Awaiting APS':
      case 'Manual Underwriting:Final Review':
        return true;
    }
    return false;
  }

  static isExpressPath(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'Accelerated Application:Issuing Policy':
      case 'Accelerated Application:Waiting for Acceptance':
      case 'Accelerated Application:Waiting for Signature':
        return true;
      case 'Haven Application:Waiting for Advisor Signature':
        return true;
    }
    return false;
  }

  static isInDocusign(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'Accelerated Application:Waiting for Signature':
      case 'TLIC Application:Waiting for Signature':
      case 'Haven Application:Waiting for Signature':
        return true;
    }
    return false;
  }

  static isTlic(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'TLIC Application:Waiting for Acceptance':
      case 'TLIC Application:Waiting for Signature':
      case 'TLIC Issued:Final Underwriting':
      case 'TLIC Issued:Issuing Policy':
      case 'TLIC Issued:Waiting for Labs':
      case 'TLIC Issued:Waiting for Policy Acceptance':
        return true;
    }
    return false;
  }

  static isKo(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'Application:Final Review':
      case 'Application:Issuing Policy':
      case 'Application:Waiting for Acceptance':
      case 'Application:Waiting for Issue Acceptance':
      case 'Haven Application:Waiting for Application Acceptance':
      case 'Haven Application:Waiting for Labs':
      case 'Haven Application:Waiting for Signature':
      case 'Manual Underwriting:Awaiting APS':
      case 'Manual Underwriting:Final Review':
        return true;
    }
    return false;
  }

  static isInProcess(workflowAction: string) {
    return this.isExpressPath(workflowAction) || this.isTlic(workflowAction) || this.isKo(workflowAction);
  }

  static isPolicyInForce(policy: Policy): boolean {
    return this.isInForce(`${policy.workflowState}:${policy.workflowSubState}`);
  }

  static isInForce(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'In Force:Free Look Period':
      case 'In Force:Grace Period':
      case 'In Force:Issued':
      case 'In Force:Pending Policy Docs':
        return true;
    }
    return false;
  }

  static isInFreeLook(workflowAction: string): boolean {
    return workflowAction === 'In Force:Free Look Period';
  }

  static isInForceIssued(workflowAction: string): boolean {
    return workflowAction === 'In Force:Issued';
  }

  static isPolicyCanceled(policy: Policy): boolean {
    return this.isCanceled(`${policy.workflowState}:${policy.workflowSubState}`);
  }

  static isCanceled(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'Application Canceled By User:Without TLIC': //Canceled By User
      case 'Application Canceled:No Labs':
      case 'Application Canceled:Not Accepted':
      case 'Application Canceled:Ran out of Time':
      case 'Funding Pause Canceled:Ran out of Time':
      case 'Application Canceled:Without TLIC':
      case 'Application Declined:Uninsurable':
      case 'Application Declined:With Vantage Option':
      case 'Application Rejected:ID Verification Failed':
      case 'Application Rejected:MVR Verification Failed':
      case 'Application Rejected:Uninsurable':
      case 'Application Rejected:With Vantage Option':
      case 'Terminated:Canceled': //Canceled By User
      case 'Terminated:Canceled - Free Look': //Canceled By User
      case 'Terminated:Conversion_completed': //term policy conversion complete
      case 'Terminated:Lapsed':
      case 'Terminated:Not Taken':
      case 'Terminated:Rescinded':
      case 'Canceled:Canceled': // annuity canceled
        return true;
    }
    return false;
  }

  static isCanceledInPa(paStatus: PolicyAdminStatus): boolean {
    if (!paStatus) {
      return false;
    }
    return /^TM/.test(paStatus.getPolicyStageCode());
  }

  static isAcceptStage(workflowAction: string): boolean {
    switch (workflowAction) {
      case 'Accelerated Application:Waiting for Acceptance':
      case 'TLIC Application:Waiting for Acceptance':
      case 'Haven Application:Waiting for Application Acceptance':
        return true;
    }
    return false;
  }

  /*
    Checks if in SA1 stage (suitability approved)
   */
  static isSuitabilityApproved(policy: Policy): boolean {
    return `${policy.workflowState}:${policy.workflowSubState}` === 'Approved:Offer ready to send';
  }

  static isPolicyInAmendStage(policy: Policy): boolean {
    return this.isAmendStage(`${policy.workflowState}:${policy.workflowSubState}`);
  }

  static isPolicyInFP1Stage(policy: Policy): boolean {
    return this.isFP1Stage(`${policy.workflowState}:${policy.workflowSubState}`);
  }

  static isPolicyInFP2Stage(policy: Policy): boolean {
    return this.isFP2Stage(`${policy.workflowState}:${policy.workflowSubState}`);
  }

  static isPolicyInFP3Stage(policy: Policy): boolean {
    return this.isFP3Stage(`${policy.workflowState}:${policy.workflowSubState}`);
  }

  static isPolicyFundingPaused(policy: Policy): boolean {
    return this.isFundingPaused(`${policy.workflowState}:${policy.workflowSubState}`);
  }

  static isPolicyEmailSent(policy: Policy): boolean {
    return this.isWaitingForPolicyAcceptance(`${policy.workflowState}:${policy.workflowSubState}`);
  }

  static isAmendStage(workflowAction: string): boolean {
    return workflowAction === 'Advisor Amendment:Waiting for Advisor Amendment';
  }

  static isFP1Stage(workflowAction: string): boolean {
    return workflowAction === 'Funding Pause:Initiate Funds Transfer';
  }

  static isFP2Stage(workflowAction: string): boolean {
    return workflowAction === 'Funding Pause:Waiting for 1035 Funds';
  }

  static isFP3Stage(workflowAction: string): boolean {
    return workflowAction === 'Funding Pause:Funds have arrived';
  }

  static isFundingPaused(workflowAction: string): boolean {
    return workflowAction.startsWith('Funding Pause:');
  }

  static isTlicOfferStage(workflowAction: string) {
    return workflowAction === 'TLIC Application:Waiting for Acceptance';
  }

  static isAcceptOfferStage(workflowAction: string) {
    return (
      workflowAction === 'TLIC Issued:Waiting for Policy Acceptance' ||
      workflowAction === 'Application:Waiting for Issue Acceptance'
    );
  }

  static isEsignOfferStage(workflowAction: string) {
    return (
      workflowAction === 'Accelerated Application:Waiting for Acceptance' ||
      workflowAction === 'Haven Application:Waiting for Application Acceptance'
    );
  }

  static isWaitingForPolicyAcceptance(workflowAction: string): boolean {
    return (
      workflowAction === 'Application:Waiting for Issue Acceptance' ||
      workflowAction === 'TLIC Issued:Waiting for Policy Acceptance' ||
      workflowAction === 'Accelerated Application:Waiting for Acceptance' ||
      workflowAction === 'Application:Waiting for Annuity Acceptance'
    );
  }

  static isInOperationsReview(workflowAction: string): boolean {
    return workflowAction === 'Operations Review:Operations Review';
  }

  static isPolicyAtAmendableWorkflowStage(workflowAction: string): boolean {
    return (
      this.isAmendStage(workflowAction) ||
      this.isWaitingForPolicyAcceptance(workflowAction) ||
      this.isFundingPaused(workflowAction)
    );
  }

  static isPolicyAmendableByAdvisor(policy: Policy): boolean {
    const isInsuredSignRequiredAtFinalAcceptance = this.isInsuredSignRequiredAtFinalAcceptance(policy);
    return this.isPolicyAtAmendableWorkflowStage(policy.getWorkflowAction()) && !isInsuredSignRequiredAtFinalAcceptance;
  }

  static isInsuredSignRequiredAtFinalAcceptance(policy): boolean {
    if (typeof policy.isAnnuity === 'function' && policy.isAnnuity()) {
      return false;
    }
    const esignMetadata = policy.insured.getInsuredSigner().esignMetadata;
    return (
      !policy.application.primaryPolicy.isOwnerSameAsInsuredOrJuvenile() &&
      esignMetadata &&
      (!!esignMetadata.linkValue || !!esignMetadata.linkGeneratedAt)
    );
  }

  static isOwnerSignRequired(policy): boolean {
    if (
      (typeof policy.isAnnuity === 'function' && policy.isAnnuity()) ||
      CoverpathUtils.isSanderlingPolicy(policy.product)
    ) {
      return false;
    }
    const esignMetadata = policy.getOwnerSigningPerson().esignMetadata;
    return (
      !policy.application.primaryPolicy.isOwnerSameAsInsuredOrJuvenile() &&
      esignMetadata &&
      !!esignMetadata.linkGeneratedAt
    );
  }

  static isICCState(state: string): boolean {
    return MetadataHierarchy.getConfigurationByChannelAndState(Product.channels.CAS.code, state, 'isICC');
  }

  /**
   * For a given policy level workflowState string, return if the policy is post-issue, i.e., in Policy Admin.
   */
  static isPostIssue(workflowState: string): boolean {
    // TODO: https://massmutual.atlassian.net/browse/HL-16837
    return /In Force/.test(workflowState);
  }

  static isInsuredAddressProp(prop: string): boolean {
    return prop.indexOf('insured.address') > 0 || prop.indexOf('insured.person.residentialAddress') > 0;
  }

  /**
   * This function returns true if the given property is a valid owner address property
   * Owner can be:
   *    1) The Insured
   *    2) A Trust (same prop as Entity)
   *    3) An Entity (same prop as Trust)
   *    4) Insured and another person (the other person's data is stored in 'otherOwner' in Policy)
   */
  static isOwnerAddressProp(prop: string): boolean {
    return (
      prop.indexOf('ownerPersonResidentialAddress') > 0 ||
      prop.indexOf('trustOrOtherEntity.legalAddress') > 0 ||
      prop.indexOf('otherOwner.residentialAddress') > 0
    );
  }

  static getPremiumDisplayObj(workflowAction: string, policy: Policy, isForAgentSite: boolean): PremiumDisplay {
    const timeFrequency =
      TimeFrequency[CommonUtils.makePaymentFrequencySafeForPolicy(policy.paymentFrequencyWithPendingAmendment())];
    const premiumAmount = Utils.formatCurrency(policy.premiumBreakdown.getTotalWithPendingAmendment().toString());
    return this.getPremiumObjForPremiumAmount(
      workflowAction,
      premiumAmount,
      policy.premiumBreakdown.baseWithPendingAmendment(),
      timeFrequency,
      policy.isTLIC(),
      isForAgentSite,
      policy.isLifeBridge()
    );
  }

  static getFlatExtraDisplayInfo(name: string, ruleCode: string, expiryAfterInYears: number) {
    //These are the avocations specified in HL-6444 for which we want to massage the display string
    //I looked for a centralized listing that we could re-use but didn't see any...
    const avocationsWithSpecialMessaging = [
      'B00613', //big game
      'B00614', //bobsledding
      'non_medical_70', //hang gliding
      'B00619', //heli skiing
      'B00623', //motorcycle racing
      'B00629', //skydiving, ultralight
    ];

    let displayText = name;
    let toolTip = 'This flat extra was added to account for additional risk associated with your policy.';
    if (ruleCode === 'occupation') {
      displayText = displayText.replace('Occupation; Answered: ', 'Occupation: ');
      toolTip =
        'You indicated that you have an occupation that increases your premium. The flat extra accounts for this risk in calculating your premium.';
    } else if (avocationsWithSpecialMessaging.indexOf(ruleCode) !== -1 && FlatExtraNames[ruleCode]) {
      displayText = `Avocation: ${FlatExtraNames[ruleCode].en}`;
      toolTip =
        'You indicated that you have an avocation that increases your premium. The flat extra accounts for this risk in calculating your premium.';
    }

    if (expiryAfterInYears) {
      const unit = expiryAfterInYears === 1 ? 'year' : 'years';
      const mask = FlatExtraMasks['temp_medical'].en;
      displayText = `${mask} (${expiryAfterInYears} ${unit})`;
    }

    return {
      name: displayText,
      toolTip: toolTip,
    };
  }

  static getPremiumObjForPremiumAmount(
    workflowAction,
    premiumAmount,
    base,
    timeFrequency,
    isTLIC,
    isForAgentSite,
    isLifeBridge
  ) {
    let premiumText: string = '';
    let showPremiumBreakdown: boolean = true;
    if (isLifeBridge) {
      premiumText = '$0';
      showPremiumBreakdown = false;
    } else {
      switch (workflowAction) {
        case 'TLIC Application:Waiting for Signature':
        case 'TLIC Application:Waiting for Acceptance':
        case 'TLIC Issued:Waiting for Labs':
          premiumText = `${premiumAmount}/${timeFrequency} (estimated)`;
          break;
        case 'Haven Application:Waiting for Application Acceptance':
        case 'Haven Application:Waiting for Signature':
        case 'Haven Application:Waiting for Labs':
        case 'Manual Underwriting:Final Review':
        case 'Manual Underwriting:Awaiting APS':
        case 'Application:Final Review':
        case 'Underwriting:Underwriting':
        case 'Haven Application:Waiting for Advisor Signature':
          premiumText = isForAgentSite ? 'Pending review' : 'Premium amount pending review';
          showPremiumBreakdown = false;
          break;
        case 'Advisor Amendment:Waiting for Advisor Amendment':
          premiumText = isForAgentSite ? `${premiumAmount}/${timeFrequency}` : 'Premium amount pending review';
          showPremiumBreakdown = false;
          break;
        case 'TLIC Issued:Final Underwriting':
          if (isTLIC) {
            premiumText = `${premiumAmount}/${timeFrequency} (estimated)`;
          } else {
            premiumText = isForAgentSite ? 'Pending review' : 'Premium amount pending review';
            showPremiumBreakdown = false;
          }
          break;
        case 'TLIC Issued:Waiting for Policy Acceptance':
        case 'Application:Waiting for Issue Acceptance':
          premiumText = `${premiumAmount}/${timeFrequency}`;
          break;
        default:
          if (premiumAmount === '$0') {
            premiumText = isForAgentSite ? 'Pending review' : 'Premium amount pending review';
          } else if (base === 0) {
            premiumText = 'Premium Pending';
            showPremiumBreakdown = false;
          } else {
            premiumText = `${premiumAmount}/${timeFrequency}`;
          }
      }
    }

    return { premiumText, showPremiumBreakdown };
  }

  static createAnEmptyPolicyStatus(): ClientPolicyStatus {
    return {
      summaryStatus: '',
      subStatus: '',
      isPostApp: false,
      isPostIssue: false,
      isInParamed: false,
      isInUnderwriting: false,
      nextPaymentDate: null,
      paidToDate: null,
      effectiveDate: null,
      tlicEffectiveDate: null,
      freeLookEndDate: null,
      nextStatus: '',
    };
  }

  static extractInvitationCode(inviteLink: string): string {
    let advisorCode: string;

    if (inviteLink) {
      const validInvitationCode: RegExp = /\?invite=([\w-]*)[^\&]*/;
      const validQuoteCode = /\?quote=([\w-]+)[^\&]*/;

      const invitationCodeMatch: RegExpMatchArray = inviteLink.match(validInvitationCode);
      const quoteCodeMatch = window.location.href.match(validQuoteCode);

      if (invitationCodeMatch && invitationCodeMatch.length > 1) {
        advisorCode = invitationCodeMatch[1];
      } else if (quoteCodeMatch && quoteCodeMatch.length > 1) {
        advisorCode = quoteCodeMatch[1];
      }
    }

    return advisorCode;
  }

  static isInsurabilityStatementSignatureRequired(policy: CoverpathBasePolicy) {
    /*
      Ideally we'd have this linked to a LaunchDarkly feature flag, but that gets a bit complicated because
      the LD Singleton can only be used server side. That means this static function (which is invoked in
      server-side and front-end code) would either either have to be linked to a controller or have the
      value of the feature flag passed in.

      To keep things simple, we will just use this const.
    */
    const covid19ResponseEnabled = true;

    if (covid19ResponseEnabled && policy.isCoverpathPolicy()) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'CoverpathUtils',
          function: 'isInsurabilityStatementSignatureRequired',
          data: {
            policyNumber: policy.policyNumber,
            policyId: policy._id,
          },
          category: UICategory.request,
        },
        'Applying Covid19Reponse override logic'
      );

      return true;
    }

    const todaysDate = moment(Utils.now());
    return (
      todaysDate.diff(moment(policy.applicationSignedDate), 'days') >= 90 ||
      policy.application.getTotalFaceAmountWithPendingAmendment() > 1000000
    );
  }

  /**
   * Use this function to decide if the owner and insured are the same person.
   * Only when owner and insured point to the same person or one of their names is missing, we consider owner and insured are the same person.
   * @return {isOwnerSameAsInsured: boolean}
   * @param ownerPerson
   * @param insuredPerson
   */
  static isOwnerSameAsInsured(ownerPerson: Person, insuredPerson: Person): boolean {
    return (
      ownerPerson &&
      insuredPerson &&
      (ownerPerson === insuredPerson || !ownerPerson.firstNameLastName || !insuredPerson.firstNameLastName)
    );
  }

  /**
   * Convenient function to provide full name (without middle name)
   * @return {full name (without middle name)}
   * @param firstName
   * @param lastName
   */
  static combineFirstLastName(firstName: string, lastName: string): string {
    const firstLastName: string = `${firstName ? firstName : ''}${lastName ? ' ' + lastName : ''}`;
    return firstLastName.trim();
  }

  /**
   * Convenient function to provide full name (with middle name)
   * @return {full name (without middle name)}
   * @param firstName
   * @param middleName
   * @param lastName
   */
  static combineFirstMiddleLastName(firstName: string, middleName: string, lastName: string): string {
    const firstLastName: string = `${firstName ? firstName : ''}${middleName ? ' ' + middleName : ''}${
      lastName ? ' ' + lastName : ''
    }`;
    return firstLastName.trim();
  }

  /**
   * Convenient function to provide first name and will factor in words that are spaced out up until the last word.
   * @return {first name}
   * @param fullName
   */
  static getFirstName(fullName: string) {
    const arr = fullName.split(' ');
    if (arr.length === 1) {
      return arr[0];
    }
    return arr.slice(0, -1).join(' ');
  }

  /**
   * Convenient function to provide last name. This will take the last word factoring out all other spaced words
   * @return {last name}
   * @param fullName
   */
  static getLastName(fullName: string) {
    const arr = fullName.split(' ');
    if (arr.length === 1) {
      return '';
    }
    return arr.slice(-1).join(' ');
  }

  /**
   * Decide what to display in the UI for owner name
   * @return {owner name for UI to display}
   * @param ownerName
   * @param insuredName
   * @param customerName
   */
  static getOwnerNameForUIDisplay(ownerName: string, insuredName: string, customerName: string): string {
    return ownerName ? ownerName : insuredName ? insuredName : customerName;
  }

  /**
   * Decide what to display in the UI for insured name
   * @return {insured name for UI to display}
   * @param insuredName
   * @param ownerName
   * @param customerName
   */
  static getInsuredNameForUIDisplay(insuredName: string, ownerName: string, customerName: string): string {
    return insuredName ? insuredName : ownerName ? ownerName : customerName;
  }

  /**
   * Get the email address for sending applications started by advisor.
   * @return {email}
   * @param customer
   */
  static getEmailForSendingAApp(customer: CoverpathCustomer): string {
    if (customer.application && customer.application.primaryPolicy.owner.email) {
      return customer.application.primaryPolicy.owner.email;
    } else if (
      customer.application.primaryPolicy.isInsuredInfoSameAsOwners() &&
      customer.primaryCustomer.person.email
    ) {
      // owner is insured
      return customer.primaryCustomer.person.email;
    } else {
      return customer.email;
    }
  }

  static getRandomMessage(messageList: Array<string>): string {
    const position = _.random(0, messageList.length - 1);
    return messageList[position];
  }

  static getAdvisorDescriptiveNoun() {
    return 'financial professional';
  }

  static getAdvisorDescriptiveShortNoun() {
    return 'producer';
  }

  /**
   * Returns the correct year to display on the Illustration
   */
  static getCurrentRateYear(): string {
    const currentRateYear =
      typeof window === 'undefined' || window === null
        ? amorphicStatic.config.nconf.get('currentRateYear')
        : (window as any).amorphic.config.currentRateYear;
    return currentRateYear ? currentRateYear : this.getDefaultCurrentRateYear();
  }

  static getDefaultCurrentRateYear(): string {
    return Utils.now().getFullYear().toString();
  }

  static getCopyrightYear(): number {
    return Utils.now().getFullYear();
  }

  static decimalToPercent(decimal: number): number {
    return (decimal / 1) * 100;
  }

  static roundToNearestThousand(input: number): number {
    return Math.round(input / 1000) * 1000;
  }

  static truncateFaceAmount(rawFaceAmount: number): number {
    const isAboveThousand = rawFaceAmount > 1000;
    const isMultipleOfThousand = rawFaceAmount % 1000 === 0;

    let roundedFaceAmount = rawFaceAmount;
    if (isAboveThousand && !isMultipleOfThousand) {
      roundedFaceAmount = CoverpathUtils.roundToNearestThousand(rawFaceAmount);
    }

    return roundedFaceAmount;
  }

  static get1035ExchangeStatus(workflowAction: string) {
    if (CoverpathUtils.isInitialProcessing(workflowAction)) {
      return undefined;
    } else if (CoverpathUtils.isInForce(workflowAction)) {
      return 'Completed';
    } else if (
      CoverpathUtils.isInProcess(workflowAction) ||
      CoverpathUtils.isInOperationsReview(workflowAction) ||
      CoverpathUtils.isAmendStage(workflowAction)
    ) {
      return 'Pending';
    }
  }

  /**
   * Returns true if workflow should move to OR1 after Accept Offer else false
   * @param policy
   */
  static shouldGoToOR1AfterAcceptOffer(policy: CoverpathBasePolicy) {
    if (!policy.applicationHasBeenSigned()) {
      return false;
    }

    return policy.insured.existingPolicies.has1035Exchange();
  }

  static shouldGenerateInsuredEsignMetadata(policy: CoverpathBasePolicy) {
    // owner is same as insured or juvi policy owner itself is the signer
    if (policy.isOwnerSameAsInsuredOrJuvenile()) {
      return false;
    }

    // Policy amended
    if (policy.hasPendingAmendment()) {
      return true;
    }

    // Policy requires insurability
    return CoverpathUtils.isInsurabilityStatementSignatureRequired(policy);
  }

  /**
   * Returns the reason (aka business case) why this particular policy is in OR1
   * @param {Policy} policy
   * @returns {ReasonForOR1}
   */

  static reasonForOR1(policy: CoverpathBasePolicy): ReasonForOR1 {
    if (policy.requiresSpecialHandling()) {
      return ReasonForOR1.USE_CASE_CRUSHER;
    } else if (policy.isJointOwnership()) {
      return ReasonForOR1.CP_JOINT_OWNERSHIP;
    } else if (CoverpathUtils.shouldGoToOR1AfterAcceptOffer(policy)) {
      return ReasonForOR1.CP_CASE;
    } else {
      return ReasonForOR1.UNKNOWN;
    }
  }

  static async isGoingToOR1(policy: CoverpathBasePolicy) {
    const excludeJointFromOR1: boolean = await this.excludeJointFromOR1(policy.policyNumber);
    const isGoingToOR1 = !excludeJointFromOR1 && policy.isJointOwnership();
    return this.shouldGoToOR1AfterAcceptOffer(policy) || policy.requiresSpecialHandling() || isGoingToOR1;
  }

  static copyInsuredToOwner(policy: CoverpathBasePolicy, customer: Customer) {
    // If insured and owner are same, point owner to insured.
    // Have to do this as the final step right before submitting the policy,
    // otherwise changes to insured type or owner tpe in the application form
    // will break.
    if (policy.isOwnerSameAsInsured() || (policy.isJointOwnership() && policy.insuredType === 'you')) {
      const financials = policy.owner.financials;
      const existingPolicies = policy.owner.existingPolicies;
      const isTINCorrect = policy.owner.isTINCorrect;
      policy.owner = policy.insured.person;

      policy.owner.financials = financials;
      policy.owner.existingPolicies = existingPolicies;
      policy.owner.isTINCorrect = isTINCorrect;

      policy.ownerPersonResidentialAddress = policy.insured.address;
      policy.ownerPersonPhone = policy.insured.phone;
    }
    if (!policy.owner.email) {
      policy.owner.email = customer.email;
    }
    if (policy.isJointOwnership() && policy.isJointOwnerSomeoneElse === 'false') {
      policy.otherOwner = policy.insured.person;
    }
  }

  /**
   * checks for:
   * - inForce
   * - TermPolicy
   * - conversionEndDate
   * - jointApp
   * @param policy
   * @param conversionEndDate
   */
  static isPolicyConvertible(policy: Policy, conversionEndDate: Date) {
    const hasNotBeenConverted = !policy.convertedTo;

    let isWithinConversionPeriod = false;
    if (conversionEndDate) {
      isWithinConversionPeriod = new Date().getTime() < conversionEndDate.getTime();
    }

    return policy.isInForce() && policy.isTermLifePolicy() && hasNotBeenConverted && isWithinConversionPeriod;
  }

  /**
   *
   * @param name
   * @returns name with all first letters capitalized.
   *
   * use-cases:
   * 1. BOSTON ST. JEAN-SPINDT 008 => Boston St. Jean-Spindt 008
   * 2. ATLANTA-ALABAMA-MCQUAID 172 => Atlanta-Alabama-Mcquaid 172
   */
  static convertToTitleCase(name: string): string | null {
    if (!name) {
      return null;
    }
    return name.toLowerCase().replace(/^(.)|\s(.)|-(.)/g, (firstLetter) => firstLetter.toUpperCase());
  }

  static getAmendmentDetailsTextForApplication(
    application: CasApplication,
    policyIsNoLongerMec: boolean = false
  ): string[] {
    let amendmentDetailsText = [];
    const primaryPolicy = application.primaryPolicy;
    if (policyIsNoLongerMec) {
      amendmentDetailsText.push(`The prior MEC Acknowledgement form is no longer applicable.`);
      if (!primaryPolicy.isSwitchedToTermLife()) {
        amendmentDetailsText.push(`Please refer to your policy and revised basic illustration.`);
      }
    }

    if (primaryPolicy.isSwitchedToJointApp()) {
      const primaryPolicyWithPendingAmendment = application.primaryPolicyWithPendingAmendment;
      const secondaryPolicyWithPendingAmendment = application.secondaryPolicyWithPendingAmendment;
      const hasECP = secondaryPolicyWithPendingAmendment.isEcpProductWithPendingAmendment();
      amendmentDetailsText.push(
        `The applied for products are now Term Life ${secondaryPolicyWithPendingAmendment.termWithPendingAmendment()} ${
          hasECP ? '- ECP' : ''
        } and a MM ${productTypes.Whole.productNames[primaryPolicyWithPendingAmendment.productWithPendingAmendment()]}.`
      );
    }

    //product is switched
    if (primaryPolicy.isSwitchedToWholeLife() || primaryPolicy.isSwitchedToTermLife()) {
      // and face amount has not changed
      if (primaryPolicy.faceAmount === primaryPolicy.faceAmountWithPendingAmendment()) {
        amendmentDetailsText.push(
          `The ${
            primaryPolicy.isWholeLifePolicyWithPendingAmendment() ? 'Whole Life' : 'Term Life'
          } face amount is ${Utils.formatCurrency(primaryPolicy.faceAmountWithPendingAmendment())}.`
        );
      }
    }

    if (primaryPolicy.isSwitchedToWholeLife() && !primaryPolicy.wholeAPLWithPendingAmendment()) {
      amendmentDetailsText.push('The application has not elected APL.');
    }

    if (primaryPolicy.isSwitchedToWholeLife()) {
      amendmentDetailsText.push(`The tax bracket is listed as ${CoverpathUtils.defaultTaxBracket}%.`);
    }

    amendmentDetailsText = amendmentDetailsText.concat(CoverpathUtils.getAmendmentDetailsTextForPolicy(primaryPolicy));
    let secondaryAmendmentDetails = [];
    if (primaryPolicy.isPartOfJointApp()) {
      secondaryAmendmentDetails = CoverpathUtils.getAmendmentDetailsTextForPolicy(application.secondaryPolicy);
    } else if (primaryPolicy.isWholeLifePolicy() && primaryPolicy.isSwitchedToJointApp()) {
      secondaryAmendmentDetails = CoverpathUtils.getAmendmentDetailsTextForPolicy(
        application.secondaryPolicyWithPendingAmendment
      );
    } else if (primaryPolicy.isTermLifePolicy() && primaryPolicy.isSwitchedToJointApp()) {
      secondaryAmendmentDetails = CoverpathUtils.getAmendmentDetailsTextForPolicy(
        application.primaryPolicyWithPendingAmendment
      );
    }

    amendmentDetailsText = amendmentDetailsText.concat(secondaryAmendmentDetails);
    return amendmentDetailsText;
  }

  static getAmendmentDetailsTextForPolicy(policy: CoverpathPolicy): string[] {
    const amendmentDetailsText: string[] = [];
    const pendingAmendments: Array<PolicyChangeHistory> = policy.getPendingAmendments();
    const typeAmendment = policy.typeWithPendingAmendment();
    const isPolicyRemovedByAmendments = !(typeAmendment === 'primary' || typeAmendment === 'secondary');
    if (typeAmendment === 'amend_remove_secondary') {
      amendmentDetailsText.push(
        'The Insured has only applied for Whole Life Insurance coverage (any reference to Term Life Insurance is removed).'
      );
    } else if (typeAmendment === 'amend_remove_primary') {
      amendmentDetailsText.push(
        'The Insured has only applied for Term Life Insurance coverage (any reference to Whole Life Insurance is removed).'
      );
    }

    if (isPolicyRemovedByAmendments) {
      return amendmentDetailsText;
    }

    pendingAmendments.forEach((change) => {
      switch (change.property) {
        case PolicyAmendmentProperty.TERM:
          if (policy.isWholeLifePolicy() || policy.isSwitchedToWholeLife() || policy.type === 'amend_add_secondary') {
            break;
          }
          if (policy.isEcpProductWithPendingAmendment()) {
            amendmentDetailsText.push(`The plan is a MM Convertible Term ${change.newValue} - ECP policy.`);
          } else {
            amendmentDetailsText.push(`The plan is a MassMutual Convertible Term ${change.newValue} year policy.`);
          }
          break;
        case PolicyAmendmentProperty.PRODUCT:
          if (policy.isSwitchedToWholeLife()) {
            amendmentDetailsText.push(
              `The applied for product is now a MM ${productTypes.Whole.productNames[change.newValue]} policy.`
            );
          } else if (policy.isSwitchedToTermLife()) {
            amendmentDetailsText.push(
              `The applied for product is now a MM Convertible Term ${policy.termWithPendingAmendment()} year ${
                policy.isEcpProductWithPendingAmendment() ? '- ECP policy' : 'policy'
              }`
            );
          } else if (policy.isWholeLifePolicy()) {
            amendmentDetailsText.push(`The plan is a MM ${productTypes.Whole.productNames[change.newValue]} policy.`);
          } else {
            //For Term Product
            if (policy.termWithPendingAmendment() === policy.term) {
              //If term did not change through amendments but ECP did change
              if (policy.isEcpProductWithPendingAmendment()) {
                amendmentDetailsText.push(`The plan is a MM Convertible Term ${policy.term} - ECP policy.`);
              } else {
                amendmentDetailsText.push(`The plan is a MassMutual Convertible Term ${policy.term} year policy.`);
              }
            }
          }
          break;
        case PolicyAmendmentProperty.HAS_GIR_RIDER:
          amendmentDetailsText.push('The policy does not have the guaranteed insurability rider.');
          break;
        case PolicyAmendmentProperty.GIR_AMOUNT:
          amendmentDetailsText.push(
            `The guaranteed insurability rider value is ${Utils.formatIntCurrency(change.newValue)}`
          );
          break;
        case PolicyAmendmentProperty.HAS_RTR_RIDER:
          amendmentDetailsText.push('The policy does not have the renewable term rider.');
          break;
        case PolicyAmendmentProperty.RTR_AMOUNT:
          amendmentDetailsText.push(`The renewable rider value is ${Utils.formatIntCurrency(change.newValue)}`);
          break;
        case PolicyAmendmentProperty.FACE_AMOUNT:
          amendmentDetailsText.push(
            `The ${
              policy.isWholeLifePolicyWithPendingAmendment() ? 'Whole Life' : 'Term Life'
            } face amount is ${Utils.formatIntCurrency(change.newValue)}.`
          );
          break;
        case PolicyAmendmentProperty.PAYMENT_FREQUENCY:
          amendmentDetailsText.push(`The payment frequency is ${change.newValue}.`);
          break;
        case PolicyAmendmentProperty.WAIVER:
          if (!change.getTypedNewValue()) {
            amendmentDetailsText.push(
              `The ${
                policy.isWholeLifePolicyWithPendingAmendment() ? 'Whole Life' : 'Term Life'
              } policy does not have the waiver of premium rider.`
            );
          } else {
            amendmentDetailsText.push(
              `The ${
                policy.isWholeLifePolicyWithPendingAmendment() ? 'Whole Life' : 'Term Life'
              } policy does have the waiver of premium rider.`
            );
          }
          break;
        case PolicyAmendmentProperty.HAS_ALIR_RIDER:
          amendmentDetailsText.push('The policy does not have the additional life insurance rider.');
          break;
        case PolicyAmendmentProperty.EXISTING_POLICY_REPLACEMENT_CASH_VALUE_TRANSFER:
          if (!!change.previousValue && change.newValue && change.getTypedNewValue() === 0) {
            amendmentDetailsText.push('No cash value will be transferred from an existing policy.');
          }
          break;
        case PolicyAmendmentProperty.ALIR_PLANNED_YEARS:
          amendmentDetailsText.push(`The additional life insurance rider is payable for ${change.newValue} years.`);
          break;
        case PolicyAmendmentProperty.ALIR_1035_EXCHANGE_AMOUNT:
          amendmentDetailsText.push(
            `The additional life insurance rider 1035 Exchange Transfer amount is ${Utils.formatIntCurrency(
              change.newValue
            )}.`
          );
          break;
        case PolicyAmendmentProperty.ALIR_1035_COST_BASIS:
          amendmentDetailsText.push(
            `The additional life insurance rider 1035 Exchange Cost Basis amount is ${Utils.formatIntCurrency(
              change.newValue
            )}.`
          );
          break;
        case PolicyAmendmentProperty.ALIR_1035_POLICY_TYPE:
          const newValue: string = Exchange1035PolicyType[change.newValue];
          amendmentDetailsText.push(`The additional life insurance rider 1035 Exchange Policy Type is ${newValue}.`);
          break;
        case PolicyAmendmentProperty.ALIR_1035_APPLY_TOWARDS: {
          let applyTowardsNewValue: string = change.newValue;
          if (change.newValue === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
            applyTowardsNewValue = 'initial premium, then ALIR';
          }
          amendmentDetailsText.push(
            `The additional life insurance rider 1035 Exchange is applied towards the ${applyTowardsNewValue}.`
          );
          break;
        }
        case PolicyAmendmentProperty.ALIR_1035_EXPECTED_ARRIVAL:
          amendmentDetailsText.push(
            `The additional life insurance rider 1035 Exchange Expected Arrival is ${change.newValue} months.`
          );
          break;
        case PolicyAmendmentProperty.ALIR_1035_IS_MEC:
          amendmentDetailsText.push(
            `The additional life insurance rider 1035 Exchange policy is ${change.newValue ? '' : 'not '}a MEC.`
          );
          break;
        case PolicyAmendmentProperty.HAS_CIR_RIDER:
          amendmentDetailsText.push('The policy does not have the Chronic Illness Rider.');
          break;
        case PolicyAmendmentProperty.CIR_AMOUNT:
          amendmentDetailsText.push(
            `The Chronic Illness Rider base benefit pool amount is ${Utils.formatIntCurrency(change.newValue)}.`
          );
          break;
        case PolicyAmendmentProperty.CIR_BENEFIT_DISBURSEMENT_METHOD:
          let disbursementMethod = '';
          if (parseInt(change.newValue) === 2) {
            disbursementMethod = '2% per month for 50 months';
          } else if (parseInt(change.newValue) === 4) {
            disbursementMethod = '4% per month for 25 months';
          } else {
            return;
          }
          amendmentDetailsText.push(`The Chronic Illness Rider benefit disbursement method is ${disbursementMethod}.`);
          break;
        case PolicyAmendmentProperty.CIR_MONTHLY_INCREASE_OPTION:
          if (policy.cirRider.puaAddedToBenefitPoolWithPendingAmendment()) {
            let increaseOption = '';
            if (Utils.parseBoolean(change.newValue)) {
              increaseOption = 'increase the Chronic Illness Rider monthly benefit amount';
            } else {
              increaseOption = 'extend the Chronic Illness Rider benefit period';
            }
            amendmentDetailsText.push(`Paid-up additions will be used to ${increaseOption}.`);
          }
          break;
        case PolicyAmendmentProperty.CIR_PREDICTED_BENEFIT_AGE:
          amendmentDetailsText.push(`The Chronic Illness Rider predicted benefit age is ${change.newValue}.`);
          break;
        case PolicyAmendmentProperty.CIR_PUA_ADDED_TO_BENEFIT_POOL:
          const puaAdditionText = Utils.parseBoolean(change.newValue) ? 'will' : 'will not';
          amendmentDetailsText.push(
            `Paid-up additions ${puaAdditionText} be added to the Chronic Illness Rider benefit pool.`
          );
          break;
        case PolicyAmendmentProperty.DIVIDEND_OPTION:
          if (!!change.newValue && change.newValue !== 'null') {
            amendmentDetailsText.push(`The selected Whole Life dividend option is ${change.newValue}.`);
          }
          break;
        case PolicyAmendmentProperty.LOAN_INTEREST_TYPE:
          if (!!change.newValue && change.newValue !== 'null') {
            amendmentDetailsText.push(`The application has elected a ${change.newValue} loan rate.`);
          }
          break;
        case PolicyAmendmentProperty.WHOLE_APL:
          if (!!change.newValue && change.newValue !== 'null') {
            amendmentDetailsText.push(`The application has elected APL.`);
          }
          break;
      }
    });

    // The ALIR amount (premium) and ALIR frequency changes are combined into one text/line of amendments.
    // If at least one of these amendments is there, we pick it and check if the other is there.
    // The values we display come from either the amendment or the rider itself.
    const alirPremiumPendingAmendment = pendingAmendments.find(
      (am) => am.property === PolicyAmendmentProperty.ALIR_PREMIUM_BASE
    );
    const alirFrequencyAmendment = pendingAmendments.find(
      (am) => am.property === PolicyAmendmentProperty.ALIR_PAYMENT_FREQUENCY
    );
    if (alirPremiumPendingAmendment || alirFrequencyAmendment) {
      const alirRider: PolicyRider = policy.alirRider;
      // the frequency is either the amended frequency or the rider frequency
      let frequency: string = alirFrequencyAmendment ? alirFrequencyAmendment.newValue : alirRider.paymentFrequency;
      frequency = CommonUtils.makePaymentFrequencySafeForPolicy(frequency) === 'annual' ? 'annually' : 'monthly';
      // the alir amount (premium) is either the amended amount or the rider amount
      const alirAmount: string = alirPremiumPendingAmendment
        ? alirPremiumPendingAmendment.newValue
        : alirRider.premiumBase.toString();
      amendmentDetailsText.push(
        `The additional life insurance rider scheduled payment amount is ${Utils.formatCurrency(
          alirAmount
        )} ${frequency}.`
      );
    }

    return amendmentDetailsText;
  }
  static swapSecondaryInformationWithPrimary(primary: Policy, secondary: Policy) {
    SWAP_FIELDS_FROM_AMENDMENT.forEach((prop) => {
      const fromPrimary = _.get(primary, prop);
      const fromSecondary = _.get(secondary, prop);
      _.set(primary, prop, fromSecondary);
      _.set(secondary, prop, fromPrimary);
    });

    primary.premiumBreakdown.flatExtras.forEach((pFE) => {
      const sFE = secondary.premiumBreakdown.flatExtras.find((fe) => fe.ruleCode === pFE.ruleCode);
      if (sFE) {
        [sFE.premium, pFE.premium] = [pFE.premium, sFE.premium];
        [sFE.amount, pFE.amount] = [pFE.amount, sFE.amount];
      } else {
        UILoggerSingleton.getInstance().warn(
          {
            module: 'CoverpathUtils',
            function: 'swapSecondaryInformationWithPrimary',
            data: {
              policyNumber: primary.policyNumber,
              policyId: primary._id,
            },
            category: UICategory.request,
          },
          `FlatExtras are not same, primaryPolicy ${primary.policyNumber} & secondaryPolicy ${secondary.policyNumber}`
        );
      }
    });

    if (primary.hasGirRider === 'true') {
      const girRider = primary.girRider;
      secondary.addRider(RiderType.GIR);
      secondary.girRider.update(girRider.premiumBase, girRider.premiumWaiver, girRider.flatExtras);
      secondary.girRider.amount = girRider.amount;
      primary.hasGirRider = 'false';
    }

    if (primary.hasRtrRider === 'true') {
      const rtrRider = primary.rtrRider;
      secondary.addRider(RiderType.RTR);
      secondary.rtrRider.update(rtrRider.premiumBase, rtrRider.premiumWaiver, rtrRider.flatExtras);
      secondary.rtrRider.amount = rtrRider.amount;
      primary.hasRtrRider = 'false';
    }

    if ((primary as CoverpathPolicy).hasCirRider) {
      const cirRider = (primary as CoverpathPolicy).cirRider;
      const secondaryAsCoverpathPolicy: CoverpathPolicy = secondary as CoverpathPolicy;
      secondaryAsCoverpathPolicy.addRider(RiderType.CIR);
      secondaryAsCoverpathPolicy.cirRider.amount = cirRider.amount;
      secondaryAsCoverpathPolicy.cirRider.update(cirRider.premiumBase, cirRider.premiumWaiver, []);
      secondaryAsCoverpathPolicy.cirRider.puaAddedToBenefitPool = cirRider.puaAddedToBenefitPool;
      secondaryAsCoverpathPolicy.cirRider.monthlyIncreaseOption = cirRider.monthlyIncreaseOption;
      secondaryAsCoverpathPolicy.cirRider.predictedBenefitAge = cirRider.predictedBenefitAge;
      secondaryAsCoverpathPolicy.cirRider.benefitDisbursementMethod = cirRider.benefitDisbursementMethod;
      (primary as CoverpathPolicy).hasCirRider = false;
    }

    const primaryAsCoverpathPolicy: CoverpathPolicy = primary as CoverpathPolicy;
    if (primaryAsCoverpathPolicy.hasAlirRider) {
      const alirRiderForPrimary = primaryAsCoverpathPolicy.alirRider;
      const secondaryAsCoverpathPolicy: CoverpathPolicy = secondary as CoverpathPolicy;
      secondaryAsCoverpathPolicy.addRider(RiderType.ALIR);

      const alirRiderForSecondary: PolicyRider = secondaryAsCoverpathPolicy.alirRider;
      alirRiderForSecondary.update(
        alirRiderForPrimary.premiumBase,
        alirRiderForPrimary.premiumWaiver,
        alirRiderForPrimary.flatExtras
      );
      alirRiderForSecondary.amount = alirRiderForPrimary.amount;
      alirRiderForSecondary.plannedYears = alirRiderForPrimary.plannedYears;
      alirRiderForSecondary.paymentFrequency = alirRiderForPrimary.paymentFrequency;
      alirRiderForSecondary.hasScheduledPayments = alirRiderForPrimary.hasScheduledPayments;
      alirRiderForSecondary.hasLumpSum = alirRiderForPrimary.hasLumpSum;
      alirRiderForSecondary.lumpSumAmount = alirRiderForPrimary.lumpSumAmount;

      secondaryAsCoverpathPolicy.alirLumpSumUses1035 = primaryAsCoverpathPolicy.alirLumpSumUses1035;
      if (!!alirRiderForSecondary.exchange1035) {
        const secondaryExchange1035: Exchange1035 = alirRiderForSecondary.exchange1035;
        secondaryExchange1035.cashValueTransfer = alirRiderForPrimary.exchange1035.cashValueTransfer;
        secondaryExchange1035.costBasis = alirRiderForPrimary.exchange1035.costBasis;
        secondaryExchange1035.policyType = alirRiderForPrimary.exchange1035.policyType;
        secondaryExchange1035.applyTowards = alirRiderForPrimary.exchange1035.applyTowards;
        secondaryExchange1035.expectedArrival = alirRiderForPrimary.exchange1035.expectedArrival;
        secondaryExchange1035.isMec = alirRiderForPrimary.exchange1035.isMec;
      }
      primaryAsCoverpathPolicy.hasAlirRider = false;
    }

    primary.riders = [];
    const secondaryPolicyNumber = secondary.policyNumber;
    const primaryPolicyNumber = primary.policyNumber;
    primary.policyNumber = secondaryPolicyNumber;
    secondary.policyNumber = primaryPolicyNumber;
    const primaryPolicyChanges = primary.policyChanges;
    primary.policyChanges = secondary.policyChanges;
    secondary.policyChanges = primaryPolicyChanges;
  }

  static updateApplicationWithAmendmentValues(application: Application) {
    const isJointApp = application.isJointApp();
    const isJointAppWithPendingAmendment = application.isJointAppWithPendingAmendment();
    if (!isJointApp && !isJointAppWithPendingAmendment) {
      CoverpathUtils.updatePolicyWithAmendmentValues(application.primaryPolicy as CoverpathPolicy);
    } else {
      const amendingSingleToJointApp = !isJointApp && isJointAppWithPendingAmendment;
      const primary = amendingSingleToJointApp
        ? application.primaryPolicyWithPendingAmendment
        : application.primaryPolicy;
      const secondary = amendingSingleToJointApp
        ? application.secondaryPolicyWithPendingAmendment
        : application.secondaryPolicy;
      CoverpathUtils.updatePolicyWithAmendmentValues(primary as CoverpathPolicy);
      CoverpathUtils.updatePolicyWithAmendmentValues(secondary as CoverpathPolicy);
      const dropWhole = primary.getCompletedPolicyValueForAttribute('type') === 'amend_remove_primary';
      const addWhole = primary.getOriginalPolicyValueForAttribute('type') === 'amend_add_primary';
      if (dropWhole) {
        CoverpathUtils.swapSecondaryInformationWithPrimary(primary, secondary);
        primary.type = 'primary';
        secondary.type = 'amend_remove_primary';
      }
      if (addWhole) {
        CoverpathUtils.swapSecondaryInformationWithPrimary(secondary, primary);
      }
    }
  }

  static updatePolicyWithAmendmentValues(policy: CoverpathPolicy) {
    const pendingAmendments = policy.getPendingAmendments();

    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathUtils',
        function: 'updatePolicyWithAmendmentValues',
        data: {
          policyNumber: policy.policyNumber,
          policyId: policy._id,
          numPendingPCH: pendingAmendments.length,
        },
        category: UICategory.request,
      },
      'Determining if policy has pending amendments to apply'
    );

    if (pendingAmendments.length > 0) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'CoverpathUtils',
          function: 'updatePolicyWithAmendmentValues',
          data: {
            policyNumber: policy.policyNumber,
            policyId: policy._id,
          },
          category: UICategory.request,
        },
        'Applying changes to policy'
      );

      policy.type = policy.typeWithPendingAmendment();
      policy.term = policy.termWithPendingAmendment();
      policy.paymentFrequency = policy.paymentFrequencyWithPendingAmendment();
      policy.waiver = policy.hasWaiverWithPendingAmendment();
      policy.faceAmount = policy.faceAmountWithPendingAmendment();
      policy.premiumBreakdown.waiver = policy.premiumBreakdown.waiverAmountWithPendingAmendment();
      policy.premiumBreakdown.base = policy.premiumBreakdown.baseWithPendingAmendment();
      policy.product = policy.productWithPendingAmendment();
      policy.productType = policy.productTypeWithPendingAmendment();
      policy.hasEcp = policy.isEcpProductWithPendingAmendment();
      policy.effectiveDate = policy.effectiveDateWithPendingAmendment();
      policy.wholeAPL = policy.wholeAPLWithPendingAmendment();
      policy.dividendOption = policy.dividendOptionWithPendingAmendment();
      policy.loanInterestType = policy.loanInterestTypeWithPendingAmendment();
      policy.productPremiumVersion = policy.productPremiumVersionWithPendingAmendment();

      const flatExtraPCH = policy.policyChanges.find(
        (item) =>
          item.property === PolicyAmendmentProperty.FLAT_EXTRA_JSON &&
          item.status === POLICY_CHANGE_HISTORY_STATUS.PENDING
      );

      if (!!flatExtraPCH) {
        const flatExtraAmendments: CollectionItemChange[] = JSON.parse(flatExtraPCH.newValue);
        flatExtraAmendments.forEach((change) => {
          const modelValue = policy.premiumBreakdown.flatExtras.find(
            (val) => val[change.uniquePropertyName] === change.uniquePropertyValue
          );

          if (modelValue) {
            modelValue[change.propBeingChanged] = change.newValue;
          } else {
            throw new Error(`Problem applying flatExtra amendment for ${change.uniquePropertyName}`);
          }
        });
      }

      policy.hasGirRider = policy.hasGirRiderWithPendingAmendment().toString();
      if (policy.girRider) {
        policy.girRider.amount = policy.girRider.amountWithPendingAmendment();
        policy.girRider.update(
          policy.girRider.premiumBaseWithPendingAmendment(),
          policy.girRider.premiumWaiverWithPendingAmendment(),
          []
        );
      }

      policy.hasRtrRider = policy.hasRtrRiderWithPendingAmendment().toString();
      if (policy.rtrRider) {
        policy.rtrRider.amount = policy.rtrRider.amountWithPendingAmendment();
        policy.rtrRider.update(
          policy.rtrRider.premiumBaseWithPendingAmendment(),
          policy.rtrRider.premiumWaiverWithPendingAmendment(),
          policy.rtrRider.flatExtrasWithPendingAmendment()
        );
      }

      if (policy.alirRider && !policy.hasAlirRiderWithPendingAmendment()) {
        // alir rider is being removed.
        // we need to find the replacement policy and set the cashValueTransfer
        const existingPolicyToModify: ExistingPolicy = policy.insured.existingPolicies.getCashValueReplacement();
        if (existingPolicyToModify) {
          existingPolicyToModify.cashValueTransfer = 0;
        }
      }

      policy.hasAlirRider = policy.hasAlirRiderWithPendingAmendment();
      if (policy.alirRider) {
        const alirRider: PolicyRider = policy.alirRider;
        alirRider.amount = policy.alirRider.amountWithPendingAmendment();
        alirRider.plannedYears = policy.alirRider.plannedYearsWithPendingAmendment();
        alirRider.paymentFrequency = policy.alirRider.paymentFrequencyWithPendingAmendment();
        alirRider.update(policy.alirRider.premiumBaseWithPendingAmendment(), 0, []);

        const alir1035Exchange: Exchange1035 = alirRider.exchange1035;
        if (alir1035Exchange && alirRider.hasAlir1035ExchangeWithPendingAmendment()) {
          alir1035Exchange.cashValueTransfer = alirRider.alir1035ExchangeAmountWithPendingAmendment();
          alir1035Exchange.costBasis = alirRider.alir1035CostBasisWithPendingAmendment();
          alir1035Exchange.policyType = alirRider.alir1035PolicyTypeWithPendingAmendment();
          alir1035Exchange.applyTowards = alirRider.alir1035ApplyTowardsWithPendingAmendment();
          alir1035Exchange.expectedArrival = alirRider.alir1035ExpectedArrivalWithPendingAmendment();
          alir1035Exchange.isMec = alirRider.alir1035IsMecWithPendingAmendment();
          const existingPolicyToModify: ExistingPolicy = policy.insured.existingPolicies.getCashValueReplacement();
          if (existingPolicyToModify) {
            existingPolicyToModify.cashValueTransfer = alir1035Exchange.cashValueTransfer;
          }
        }
      }

      policy.hasCirRider = policy.hasCirRiderWithPendingAmendment();
      if (policy.cirRider) {
        policy.cirRider.amount = policy.cirRider.amountWithPendingAmendment();
        policy.cirRider.benefitDisbursementMethod = policy.cirRider.benefitDisbursementMethodWithPendingAmendment();
        policy.cirRider.predictedBenefitAge = policy.cirRider.predictedBenefitAgeWithPendingAmendment();
        policy.cirRider.puaAddedToBenefitPool = policy.cirRider.puaAddedToBenefitPoolWithPendingAmendment();
        policy.cirRider.monthlyIncreaseOption = policy.cirRider.monthlyIncreaseOptionWithPendingAmendment();
        policy.cirRider.update(
          policy.cirRider.premiumBaseWithPendingAmendment(),
          policy.cirRider.premiumWaiverWithPendingAmendment(),
          []
        );
      }
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathUtils',
        function: 'updatePolicyWithAmendmentValues',
        data: {
          policyNumber: policy.policyNumber,
          policyId: policy._id,
        },
        category: UICategory.request,
      },
      'Updating status on policy change history records'
    );

    pendingAmendments.forEach((pch) => {
      if (pch.status === POLICY_CHANGE_HISTORY_STATUS.PENDING) {
        pch.status = POLICY_CHANGE_HISTORY_STATUS.COMPLETE;
      }
    });
  }

  // Age 18-44 for term 30 && tobacco, 18-53 for others
  static isEcpEligible(policy: Policy): boolean {
    // If dob is not available, return false.
    if (!policy.insured.person.dob) {
      return false;
    }

    const insuredDob: Date = new Date(policy.insured.person.dob);
    const isSmoker: boolean = CommonUtils.isSmokerHealthClassFromPolicy(policy);

    return (
      CommonUtils.isOlderThanOrEqualToAge(insuredDob, Product.productSpecs.CASTermECP.minAge) &&
      (isSmoker && policy.term === 30
        ? CommonUtils.isYoungerThanAge(
            insuredDob,
            Product.products.CASTermECP.productSpecs.termBreakdown[policy.term].tobaccoMaxAge
          )
        : CommonUtils.isYoungerThanAge(
            insuredDob,
            Product.products.CASTermECP.productSpecs.termBreakdown[policy.term].maxAge
          ))
    );
  }

  static shouldShowAccountCenterTemplate(policy): boolean {
    return policy.isJointOwnership() || CoverpathUtils.shouldGoToOR1AfterAcceptOffer(policy);
  }

  static getProductDescription(isTerm: boolean, term: number, useShort?: boolean, product?: string): string {
    if (isTerm) {
      return this.getTermProductDescription(term, useShort, product);
    }
    return this.getWholeLifeProductDescription(term, useShort, product);
  }
  // Helper function that returns Annually Renewable Term or Convertible Term xx depending on term value
  // A.R.T. is basically the same as the other 'Term' products but with term = 1
  // We don't want to display Convertible Term 1 but Annually Renewable Term in this case
  static getTermProductDescription(term: number, useShort?: boolean, product?: string): string {
    if (term === 1) {
      if (product && Product.products[product] && Product.products[product].artDescription) {
        return useShort ? Product.products[product].artDescriptionShort : Product.products[product].artDescription;
      } else {
        return useShort ? Product.products.CASTerm.artDescriptionShort : Product.products.CASTerm.artDescription;
      }
    }
    if (product && Product.products[product] && Product.products[product].shortName) {
      return useShort
        ? Product.products[product].shortName.replace('$Term$', term)
        : Product.products[product].longName.replace('$Term$', term);
    }
    return useShort
      ? Product.products.CASTerm.shortName.replace('$Term$', term.toString())
      : Product.products.CASTerm.longName.replace('$Term$', term.toString());
  }

  static getWholeLifeProductDescription(term: number, useShort?: boolean, product?: string): string {
    if (product && Product.products[product] && Product.products[product].shortName) {
      return useShort
        ? Product.products[product].shortName.replace('$Term$', term)
        : Product.products[product].longName.replace('$Term$', term);
    }
    return useShort
      ? Product.products.CPL100.shortName.replace('$Term$', term.toString())
      : Product.products.CPL100.longName.replace('$Term$', term.toString());
  }

  // To make calling easy. Actual work is done by getProductDescription
  static getCACProductDescriptionFromPolicy(policy: CoverpathBasePolicy): string {
    return CoverpathUtils.getCACProductDescription(
      CoverpathUtils.isSanderlingPolicy(policy.productWithPendingAmendment()),
      policy.productWithPendingAmendment(),
      policy.isWholeLifePolicyWithPendingAmendment(),
      policy.termWithPendingAmendment(),
      policy.isEcpProductWithPendingAmendment(),
      policy.isLifeBridge()
    );
  }
  static getCACProductDescription(
    isSanderling: boolean,
    product: string,
    isWholeLife: boolean,
    term: number,
    isECP: boolean,
    isLifeBridge: boolean
  ): string {
    if (isWholeLife) {
      return `Whole Life${isSanderling ? ' Legacy ' : ' '}${term}`;
    } else if (term === 1) {
      const isVantage = isSanderling && !(product === Product.channels.MMOPM.productCodes.MMOPMTermAPT80);
      return `${isVantage ? 'Vantage ' : ''}Annually Renewable Term`;
    } else if (product === Product.channels.MMOPM.productCodes.MMOPMTerm) {
      return 'Term 20 Guaranteed';
    } else {
      return `${isSanderling ? 'Vantage ' : isLifeBridge ? 'Term ' : 'Convertible Term '}${term}${isECP ? ' ECP' : ''}`;
    }
  }

  static copyCustomerEmailToOwner(policy: Policy, customer: Customer) {
    if (policy.getOwnerEmail() !== customer.email) {
      policy.owner.email = customer.email;
      policy.owner.emailTrigger();
    }
  }

  static getInsuredDob(application: Application): Date {
    if (application.primaryPolicy.insured.person.dob) {
      return application.primaryPolicy.insured.person.dob;
    } else {
      return application.quote.dateOfBirth;
    }
  }

  static hasAdvisorSentFinalOfferToCustomer(policy: Policy): boolean {
    return (
      CoverpathUtils.isWaitingForPolicyAcceptance(`${policy.futureWorkflowState}:${policy.futureWorkflowSubState}`) ||
      CoverpathUtils.isPolicyEmailSent(policy)
    );
  }

  static isLowFaceWholeLife(policy: Policy): boolean {
    if (
      !policy ||
      !policy.isCoverpathPolicy() ||
      !policy.isWholeLifePolicy() ||
      policy.isPartOfJointApp() ||
      !LowFaceWholeLife.availableProducts.includes(policy.product) ||
      !policy.insured.person.dob
    ) {
      return false;
    }
    const amountAtRisk = CoverpathUtils.getTotalAmountAtRisk(policy as CoverpathPolicy);
    return amountAtRisk >= LowFaceWholeLife.minFace && amountAtRisk < LowFaceWholeLife.maxFace;
  }

  static getTotalAmountAtRisk(policy: CoverpathPolicy): number {
    return WholeLifeValidationUtils.getTotalFaceAmountWithRiders(
      policy.getTotalFaceAmount() || 0,
      policy.rtrRiderAmount || 0,
      policy.alirRider ? policy.alirRider.amount : 0,
      policy.alirRider && policy.alirRider.exchange1035 ? policy.alirRider.exchange1035.cashValueTransfer : 0,
      policy.insured.person.getInsuranceAge()
    );
  }

  static getPolicyIds(record: AccountCenterListRecordView): string[] {
    const policyIds = [record.policyId];
    if (record.secondaryPolicyDetails) {
      policyIds.push(record.secondaryPolicyDetails.policyId);
    }
    return policyIds;
  }

  static getInsuranceAgeDate(dateOfBirth, asOfDate) {
    asOfDate = asOfDate || Utils.now();
    return Utils.getCurrentMidYearDate(dateOfBirth, asOfDate);
  }

  static getInsuranceAge(dateOfBirth, asOfDate?) {
    asOfDate = asOfDate || Utils.now();
    const insDate = this.getInsuranceAgeDate(dateOfBirth, asOfDate);
    return Utils.yearsBetween(dateOfBirth, asOfDate) + (insDate <= asOfDate ? 1 : 0);
  }

  static calculateEffectiveBackDate(exchange1035Enabled: boolean, dateOfBirth: Date | null, signedDate?: Date) {
    let maxEffectiveDate = signedDate || Utils.now();
    maxEffectiveDate.setHours(10);

    let monthsToBackdate = 6;

    if (exchange1035Enabled) {
      monthsToBackdate = 4;
    }

    maxEffectiveDate = Utils.offsetDate(maxEffectiveDate, -monthsToBackdate, 'months');

    // make sure effective date is between the 1st and 28th
    if ([29, 30, 31].includes(maxEffectiveDate.getDate())) {
      maxEffectiveDate.setDate(1);
      maxEffectiveDate.setMonth(maxEffectiveDate.getMonth() + 1);
    }

    if (dateOfBirth) {
      const insuranceAgeToday = this.getInsuranceAge(dateOfBirth, Utils.now());
      const pastInsuranceAge = this.getInsuranceAge(dateOfBirth, maxEffectiveDate);

      if (insuranceAgeToday === 18 && pastInsuranceAge === 17) {
        // we should not backdate to juvi
        return null;
      }

      // only backdate if your insurance age changed
      if (pastInsuranceAge < insuranceAgeToday) {
        // should backdate to right before the insurance age changed
        const insuranceAgeDate = this.getInsuranceAgeDate(dateOfBirth, signedDate);
        let effectiveDate = insuranceAgeDate;
        effectiveDate = Utils.offsetDate(effectiveDate, -1, 'days');
        if ([29, 30, 31].includes(effectiveDate.getDate())) {
          effectiveDate.setDate(28);
        }
        effectiveDate.setHours(10);
        UILoggerSingleton.getInstance().info(
          {
            module: 'CoverpathUtils',
            function: 'calculateEffectiveBackDate',
            data: {
              effectiveDate: effectiveDate,
              maxEffectiveDate: maxEffectiveDate,
              insuranceAgeToday,
              pastInsuranceAge,
              insuranceAgeDate,
            },
            category: UICategory.request,
          },
          'Calculated effective backdate'
        );

        if (effectiveDate > maxEffectiveDate) {
          return effectiveDate;
        } else {
          return maxEffectiveDate;
        }
      }
    }

    return null;
  }

  static isDesktop() {
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  static isPhone() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  static getAmendmentFormNameByState(state: string) {
    switch (state) {
      case 'DE':
      case 'ND':
      case 'SD':
        return 'AADTC-GEN';
      case 'FL':
        return 'AADTC-FL';
      case 'CA':
        return 'AADTC-CA';
      default:
        return 'ICC17AADTC';
    }
  }

  static getInsurabilityFormNameByState(state: string) {
    switch (state) {
      case 'DE':
      case 'ND':
      case 'SD':
        return 'ISDTC_DE_ND_SD_Insurability_Statement';
      case 'FL':
        return 'ISDTC_FL_Insurability_Statement';
      case 'CA':
        return 'ISDTC_CA_Insurability_Statement';
      default:
        return 'ICC17ISDTC_Insurability_Statement';
    }
  }

  static getConversionFormNameByState(state: string) {
    switch (state) {
      case 'DE':
      case 'ND':
      case 'SD':
        return 'TCV0120_Simplified_Conversion';
      case 'CA':
        return 'A1002GEN_Simplified_Conversion';
      case 'FL':
        return 'A1002FL_Simpified_Conversion';
      default:
        return 'ICC12A1002_Simplified_Conversion';
    }
  }

  static getConsentFormNameByState(state: string) {
    if (state === 'CA') {
      return 'MM_California_Approved_E-Delivery_Opt_In_Form';
    } else {
      return 'Post_Issue_Electronic_Consent';
    }
  }

  static getPappFormNameByState(state: string) {
    return 'MM_(PAPP)_Payment_Plan_Pre_Authorization_Form';
  }

  static getAgencyDisplayName(agency: AgencyCorpsRelationship): string {
    let displayName = '';
    if (agency.corpsName) {
      displayName += agency.corpsName;
      if (agency.agencyName) {
        displayName += `/${agency.agencyName}`;
      } else if (agency.agencyBpid) {
        displayName += `/${agency.agencyBpid}`;
      }
    } else {
      if (agency.corpsBpid) {
        displayName += `${agency.corpsBpid}/`;
      }

      if (agency.agencyName) {
        displayName += `${agency.agencyName}`;
      } else {
        displayName += `${agency.agencyBpid}`;
      }
    }

    return displayName;
  }

  /**
   * This function constructs a small object we POST to PdfStream to give it the info it needs to create a mostly-filled amendments PDF
   * We take this approach to avoid adding thousands of lines of config to pdfStreamExport.json :)
   */
  static getCoverpathAmendmentsFormData(policy: CoverpathPolicy, policyIsNoLongerMec: boolean = false) {
    const formName = `${this.getAmendmentFormNameByState(policy.getApplicationState())}.pdf`;
    const amendmentDetailsText: string[] = this.getAmendmentDetailsTextForApplication(
      policy.application,
      policyIsNoLongerMec
    );
    const insuredSignerName = policy.isOwnerSameAsInsuredOrJuvenile()
      ? policy.getSignerFullName()
      : policy.insured.getInsuredSigner().fullName;

    //"top" and "bottom" refer to relative placement on the form
    const bottomPolicyNumber = policy.application.primaryPolicyWithPendingAmendment.policyNumber;
    let topPolicyNumber = '';

    if (policy.application.isJointAppWithPendingAmendment()) {
      topPolicyNumber = `${policy.application.secondaryPolicyWithPendingAmendment.policyNumber} /`;
    }

    return {
      templateName: formName,
      formOrder: 0,
      roles: ['producer', 'owner', 'insured'],
      formType: 'pdfTemplate',
      type: 'Amendment',
      mappings: {
        'Amendment_Field1': amendmentDetailsText.join(' '),
        'Policy Number Top': topPolicyNumber,
        'Policy Number Bottom': bottomPolicyNumber,
        'Appl Dated': Utils.formatDate(policy.submittedAt),
        'Proposed Insured': policy.insured.person.getFullName(),
        'OwnerSignaturePrintedName': policy.getOwnerSigningName(),
        'InsuredSignaturePrintedName': insuredSignerName,
      },
    };
  }

  static getFormDataByType(formType: string) {
    if (formType === 'Conversion') {
      return this.getCoverpathConversionFormData;
    } else if (formType === 'Papp') {
      return this.getPappFormData;
    } else if (formType === 'Consent') {
      return this.getConsentFormData;
    }
  }

  static getCoverpathConversionFormData(
    policy: CoverpathPolicy,
    floridaStateLicense?: string,
    agentCountyCityStateString?: string
  ) {
    const ownerState = policy.ownerPersonResidentialAddress.state;
    const templateName = `${CoverpathUtils.getConversionFormNameByState(
      policy.ownerPersonResidentialAddress.state
    )}.pdf`;

    const convertedFromPolicy = this.getConvertedFromPolicy(policy);
    const termPolicyFaceAfterConversion = convertedFromPolicy.faceAmount - policy.faceAmount;
    const conversionType = termPolicyFaceAfterConversion === 0 ? 'Full' : 'Partial';
    const balanceContinued = termPolicyFaceAfterConversion < 100000 ? 0 : termPolicyFaceAfterConversion;
    const balanceTerminated = termPolicyFaceAfterConversion < 100000 ? termPolicyFaceAfterConversion : 0;
    const discontinuePremiumWaiver = convertedFromPolicy.waiver && !policy.waiver;

    const mappings = {
      Insured1_FullName: policy.insured.person.getFullName(),
      Insured1_DOB: Utils.formatDate(new Date(policy.insured.person.dob)),
      Conversion_PremiumPaidByWaiver: 'No',
      Owner1_FullName: policy.isOwnerSameAsInsured() ? '' : policy.getOwnerSigningName(),
      ConversionFrom_DiscontinuePremiumWaiver: discontinuePremiumWaiver,
      ConversionFrom_Type: conversionType,
      ConversionFrom_Policy1_Number: policy.convertedFrom,
      ConversionFrom_Policy1_Type: 'Policy',
      ConversionFrom_Policy1_AmountConverted: Utils.formatCurrency(policy.faceAmount, 0, false),
      ConversionFrom_Policy1_BalanceContinued: Utils.formatCurrency(balanceContinued, 0, false),
      ConversionFrom_Policy1_BalanceTerminated: Utils.formatCurrency(balanceTerminated, 0, false),
      ConversionTo_ExistingPolicy_Type: '',
      ConversionTo_ExistingPolicy_Number: '',
      ConversionTo_ExistingPolicy_IncreaseAmount: '',
      ConversionTo_ExistingPolicy_IncreaseType: '',
      ConversionTo_NewPolicy_Type: 'WholeLife',
      ConversionTo_NewPolicy_Plan: policy.term,
      ConversionTo_NewPolicy_FaceAmount: Utils.formatCurrency(policy.faceAmount, 0, false),
      ConversionTo_NewPolicy_Frequency: this.makePaymentFrequencySafeForPolicyStrict(policy.paymentFrequency),
      ConversionTo_NewPolicy_PolicyDate: policy.effectiveDate
        ? Utils.formatDate(policy.effectiveDate)
        : Utils.formatDate(new Date()),
      ConversionTo_NewPolicy_AutomaticPremiumLoan: !!policy.wholeAPL,
      ConversionTo_NewPolicy_AutomaticPremiumLoan_LoanRate: policy.loanInterestType,
      SignatureState_A1002: ownerState === 'FL' ? agentCountyCityStateString : ownerState,
      ExistingOwner1_FullName: policy.getOwnerSigningName(),
      ExistingOwner1_SignatureDate_A1002: Utils.formatDate(new Date()),
      ExistingOwner1_Title: '',
      ExistingOwner2_FullName: '',
      ExistingOwner2_SignatureDate_A1002: '',
      ExistingOwner2_Title: '',
      Producer1_FullName: (policy.primaryPolicyAgent.agent.party as Person).fullName,
      Producer1_SignatureDate_A1002: '',
      Producer1_FloridaLicenseIDNumber: ownerState === 'FL' ? floridaStateLicense : '',
    };

    if (policy.dividendOption) {
      if (['PUA', 'Cash'].includes(policy.dividendOption)) {
        mappings['ConversionTo_NewPolicy_DividendOption'] = policy.dividendOption;
      } else {
        mappings['ConversionTo_NewPolicy_DividendOption'] = 'Other';
        mappings['ConversionTo_NewPolicy_DividendOption_Other'] = policy.dividendOption;
      }
    }

    return {
      templateName,
      formOrder: 0,
      roles: ['producer', 'owner'],
      formType: 'pdfTemplate',
      type: 'Conversion',
      mappings,
    };
  }

  static getPappFormData(policy: CoverpathPolicy, SSN?: string) {
    const templateName = `${CoverpathUtils.getPappFormNameByState(policy.getApplicationState())}.pdf`;

    const mappings = {
      PolicyNumber: policy.policyNumber,
      Insured_FullName: policy.insured.person.getFullName(),
      Payment_OneTimeInitial: '',
      Payment_PappRecurring: policy.isAutoPay ? 'Yes' : '',
      Payment_OneTimeEFT: '',
      Payment_ChangeBank: '',
      Payment_ChangeDraftDay: '',
      PayFrequency: this.makePaymentFrequencySafeForPolicyStrict(policy.paymentFrequency),
      Payer_FullName: policy.getOwnerSigningName(),
      Payer_Type: policy.paymentAccountOwnerType,
      Payer_Address: Utils.formatViewAddress(policy.getPremiumPayerAddress()),
      Payer_EmailAddress: policy.ownerPerson.email,
      Payer_TaxID: SSN,
      Payer_DOB: Utils.formatDate(new Date(policy.ownerPerson.dob)),
      Account_Type: policy.paymentAccountType,
      CheckingNumber: policy.paymentABA,
      AccountNumber: policy.paymentAccount,
      BankName: policy.paymentBankName,
      DraftDate: policy.paymentDay || '',
      Date: Utils.formatDate(new Date()),
      Entity_Name: '',
    };

    return {
      templateName,
      formOrder: 1,
      roles: ['producer', 'owner'],
      formType: 'pdfTemplate',
      type: 'PAPP',
      mappings,
    };
  }

  static getConsentFormData(policy: CoverpathPolicy) {
    const templateName = `${CoverpathUtils.getConsentFormNameByState(policy.getApplicationState())}.pdf`;

    const mappings = {
      'Policy Number': policy.policyNumber,
      'Insureds Name': policy.insured.person.getFullName(),
      'Printed Name of Owner': policy.getOwnerSigningName(),
      'Date Signed': Utils.formatDate(new Date()),
      'Printed Name of Additional Owner if applicable': '',
      'Date Signed_2': '',
      'Email address': policy.owner.email,
    };

    return {
      templateName,
      formOrder: 2,
      roles: ['owner'],
      formType: 'pdfTemplate',
      type: 'Consent',
      mappings,
    };
  }

  static getCoverpathInsurabilityFormData(policy: CoverpathPolicy) {
    const templateName = `${CoverpathUtils.getInsurabilityFormNameByState(policy.getApplicationState())}.pdf`;
    const insuredSignerName = policy.isOwnerSameAsInsuredOrJuvenile()
      ? policy.getSignerFullName()
      : policy.insured.getInsuredSigner().fullName;

    //When joint app, use `Primary Policy No` and `Secondary Policy No` leave `Policy No` empty
    //When not joint app, only use `Policy No`
    const mappings = {
      'Policy No': !policy.application.isJointAppWithPendingAmendment() ? policy.policyNumber : '',
      'Appl Dated': Utils.formatDate(policy.submittedAt),
      'Proposed Insured': policy.insured.person.getFullName(),
      'OwnerSignaturePrintedName': policy.getOwnerSigningName(),
      'Primary Policy No': policy.application.isJointAppWithPendingAmendment()
        ? policy.application.primaryPolicyWithPendingAmendment.policyNumber
        : '',
      'Secondary Policy No': policy.application.isJointAppWithPendingAmendment()
        ? policy.application.secondaryPolicyWithPendingAmendment.policyNumber
        : '',
      'InsuredSignaturePrintedName': insuredSignerName,
    };

    return {
      templateName,
      formOrder: 0,
      roles: ['producer', 'owner', 'insured'],
      formType: 'pdfTemplate',
      type: 'InsurabilityStatement',
      mappings,
    };
  }

  static getCoverpathMecFormData(policy: CoverpathPolicy) {
    const templateName: string = 'a5030_mec.pdf';

    const mappings = {
      PlanName: policy.getProductNameWithPendingAmendment(),
      InsuredName: policy.insured.person.getFullName(),
      TaxId1: policy.getOwnerTaxId(),
    };

    if (policy.isJointOwnership()) {
      mappings['TaxId2'] = policy.getSecondOwnerTaxId();
    }

    return {
      templateName,
      formOrder: 0,
      roles: ['owner'],
      formType: 'pdfTemplate',
      type: 'Mec',
      mappings,
    };
  }

  static getDateInPrettyFormat() {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en', { month: 'long' });
    return formatter.format(date) + ' ' + date.getDate() + ', ' + date.getFullYear();
  }

  /**
   * Returns date string in UTC time zone. This allows for us to show the user a consistent date instead of displayed date being dependent on the user's local timezone
   *
   * @param   {Date}  date              Input date object
   *
   * @return  {string | undefined}      Date string in UTC time zone, eg "11/03/1995". Returns undefined if no date is provided
   */
  static getUTCDateString(date: Date): string | undefined {
    if (!date) {
      return undefined;
    }

    const utcMonth = `${date.getUTCMonth() + 1}`.padStart(2, '0');
    const utcDate = `${date.getUTCDate()}`.padStart(2, '0');
    const utcYear = `${date.getUTCFullYear()}`.padStart(4, '0');

    return `${utcMonth}/${utcDate}/${utcYear}`;
  }

  // This function should include all Sanderling policy types, both term life and whole life
  static isSanderlingPolicy(product: string): boolean {
    return (
      product === Product.products.MMTRADTerm.code ||
      product === Product.products.MMOPMTerm.code ||
      product === Product.products.MMOPMTermAPT80.code ||
      product === Product.products.MMTRADTermECP.code ||
      product === Product.products.MMTRADWL100.code ||
      product === Product.products.MMTRADWL65.code ||
      product === Product.products.MMTRADWL20.code
    );
  }

  /**
   * Get a JS Date from a string that we think represents a date.
   * Return undefined if string format looks funky
   * @param dateString
   */
  static coerceStringToDate(dateString: string): Date | undefined {
    const fromISO = DateTime.fromISO(dateString);

    if (fromISO.isValid) {
      return fromISO.toJSDate();
    }

    //e.g, 25-Dec-2020
    const fromDDMMMYYYY = DateTime.fromFormat(dateString, 'dd-MMM-yyyy', { zone: 'UTC' });
    if (fromDDMMMYYYY.isValid) {
      return fromDDMMMYYYY.toJSDate();
    }

    //e.g., 12-25-2020
    const fromMMDDYYYY = DateTime.fromFormat(dateString, 'MM-dd-yyyy', { zone: 'UTC' });
    if (fromMMDDYYYY.isValid) {
      return fromMMDDYYYY.toJSDate();
    }

    return undefined;
  }

  /** Waits until the condition becomes true or times out.
   * Condition should return a boolean value or a Promise that resolves to a boolean value */
  static async waitUntil(condition: () => boolean | Promise<boolean>, interval = 500, timeout = 3000) {
    return new Promise((resolve, reject) => {
      const intervalId = setInterval(async () => {
        UILoggerSingleton.getInstance().info(
          {
            module: 'CoverpathUtils',
            function: 'waitUntil',
            category: UICategory.request,
          },
          'Reevaluating the condition.'
        );
        const val = await condition();
        if (val) {
          // condition met. Cancel timer and resolve promise
          clearInterval(intervalId);
          resolve(val);
        }
      }, interval);

      // timeout after 3 min (default) and reject the promise if not already settled.
      setTimeout(() => {
        clearInterval(intervalId);
        reject(new Error("Condition didn't become true in time."));
      }, timeout);
    });
  }

  static isPremiumMoreThanAppliedFor(policy) {
    if (policy.insuredIsJuvenile()) {
      return false;
    }

    const primaryPolicyWithPendingAmendment = policy.application.primaryPolicyWithPendingAmendment;
    const maxAnnualPremium = primaryPolicyWithPendingAmendment.maxAnnualPremium;
    const application = policy.application;
    return primaryPolicyWithPendingAmendment.paymentFrequencyWithPendingAmendment().toLowerCase() === 'annual'
      ? maxAnnualPremium < application.getTotalPremiumWithPendingAmendment()
      : maxAnnualPremium / 12 < application.getTotalPremiumWithPendingAmendment();
  }

  static isPremiumMoreThanAppliedForBeforeAmendment(policy) {
    if (policy.insuredIsJuvenile()) {
      return false;
    }

    const primaryPolicy = policy.application.primaryPolicy;
    const maxAnnualPremium = primaryPolicy.maxAnnualPremium;
    const application = policy.application;
    return primaryPolicy.paymentFrequency.toLowerCase() === 'annual'
      ? maxAnnualPremium < application.getTotalPremium()
      : maxAnnualPremium / 12 < application.getTotalPremium();
  }

  static isFaceMoreThanMaxFace(policy) {
    if (policy.insuredIsJuvenile()) {
      return false;
    }

    return (
      WholeLifeValidationUtils.getTotalFaceAmountWithRiders(
        policy.application.getTotalFaceAmountWithPendingAmendment(),
        policy.hasRtrRiderWithPendingAmendment() ? policy.rtrRider.amountWithPendingAmendment() : 0,
        policy.hasAlirRiderWithPendingAmendment() ? policy.alirRider.amountWithPendingAmendment() : 0,
        null,
        policy.insured.getInsuranceAge()
      ) > policy.maxFace
    );
  }

  static isFaceMoreThanMaxFaceBeforeAmendment(policy) {
    if (policy.insuredIsJuvenile()) {
      return false;
    }

    return (
      WholeLifeValidationUtils.getTotalFaceAmountWithRiders(
        policy.application.getTotalFaceAmount(),
        policy.hasRtrRiderBool ? policy.rtrRider.amount : 0,
        policy.hasAlirRider ? policy.alirRider.amount : 0,
        null,
        policy.insured.getInsuranceAge()
      ) > policy.maxFace
    );
  }

  static createJwtToken(policy: CoverpathPolicy, timeStamp: Date, secret: string) {
    return jwt.sign(
      {
        product: policy.product,
        polno: policy.policyNumber,
        date: timeStamp.getTime(),
      },
      secret
    );
  }

  static setSignerLinkTimestamp(signer: Person, timeStamp: Date): void {
    signer.esignMetadata.validationAttempts = 0;
    signer.esignMetadata.linkGeneratedAt = timeStamp;
  }

  static isEmailString(input: string) {
    return Utils.isEmailString(input);
  }

  /**
   * De-Duplicate an array based on an object parameter
   * @param arr is an array of objects
   * ex: [{id:1, name: 'Debayan'}, {id: 2, name: 'Adeola'}, {id: 3, name: 'Marisol'}, {id: 4, name: 'Adeola'}]
   * @param predicate is a callback function that takes in the entity of arr and returns the value
   * ex: (arrVal) => arrVal.name
   * Returns an array of the same type as arr
   */
  static uniqBy(arr, predicate): any[] {
    const cb = typeof predicate === 'function' ? predicate : (o) => o[predicate];
    const res = arr
      .reduce((map, item) => {
        const key = item === null || item === undefined ? item : cb(item);
        if (!map.has(key)) {
          map.set(key, item);
        }
        return map;
      }, new Map())
      .values();
    return Array.from(res);
  }

  static isAnnuityInflationProtector(input: string): input is AnnuityInflationProtector {
    return (
      input === AnnuityInflationProtector.NONE ||
      input === AnnuityInflationProtector.ONE_PERCENT ||
      input === AnnuityInflationProtector.TWO_PERCENT ||
      input === AnnuityInflationProtector.THREE_PERCENT ||
      input === AnnuityInflationProtector.FOUR_PERCENT
    );
  }

  static isAnnuityApplicationMetadataFileName(metadata_file_name: string) {
    return Object.values(AnnuityApplicationMetadataFileName).includes(
      <AnnuityApplicationMetadataFileName>metadata_file_name
    );
  }

  static isAnnuitySuitabilityMetadataFileName(metadata_file_name: string) {
    return Object.values(AnnuitySuitabilityMetadataFileName).includes(
      <AnnuitySuitabilityMetadataFileName>metadata_file_name
    );
  }

  static isAnnuityReplacementMetadataFileName(metadata_file_name: string) {
    return Object.values(AnnuityReplacementMetadataFileName).includes(
      <AnnuityReplacementMetadataFileName>metadata_file_name
    );
  }

  static isAnnuityCertificateOfTrustMetadataFileName(metadata_file_name: string) {
    return Object.values(AnnuityCertificateOfTrustMetadataFileName).includes(
      <AnnuityCertificateOfTrustMetadataFileName>metadata_file_name
    );
  }

  static isAnnuityFR3013MetadataFileName(metadata_file_name: string) {
    return Object.values(AnnuityFR3013MetadataFileName).includes(<AnnuityFR3013MetadataFileName>metadata_file_name);
  }

  static isEditableWorkflowStage(workflowAction) {
    return (
      workflowAction === 'Consent To Underwriting:Consent To Underwriting' ||
      workflowAction === 'Tele CMI:Tele CMI Needed'
    );
  }

  static handleSsnHyphens(ssn: string, includeHyphens: boolean): string {
    const storedWithHyphens = _.includes(ssn, '-');
    const alreadyFormatted = (includeHyphens && storedWithHyphens) || (!includeHyphens && !storedWithHyphens);

    if (!alreadyFormatted && includeHyphens) {
      ssn = [ssn.slice(0, 3), ssn.slice(3, 5), ssn.slice(5)].join('-');
    } else if (!alreadyFormatted && !includeHyphens) {
      ssn = ssn.replace(/-/g, '');
    }

    return ssn;
  }

  static getDividendOptionFullName(dividendOption: string) {
    if (dividendOption === 'PUA') {
      return 'Paid-up Additions';
    } else if (dividendOption === 'PRPUA') {
      return 'Reduce Premium/Paid-up Additions';
    } else if (dividendOption === 'PRCash') {
      return 'Reduce Premium/Cash';
    } else if (dividendOption === 'Accumulate') {
      return 'Dividend Accumulations';
    } else {
      return dividendOption;
    }
  }

  static isTPDProducer(producerId: string): boolean {
    return producerId && producerId.toLowerCase().indexOf('tpd') === 0;
  }

  /** capping rate class to standard on term to whole life conversion **/
  static getRateClassForLowFaceWholeLifeOnConversion(termPolicyRateClass: string, wholeLifeFaceAmount: number): string {
    if (wholeLifeFaceAmount < LowFaceWholeLife.maxFace) {
      if (termPolicyRateClass === RateClasses.SELECT_TOBACCO.code) {
        return RateClasses.STANDARD_TOBACCO.code;
      } else if (termPolicyRateClass === RateClasses.SELECT.code) {
        return RateClasses.STANDARD.code;
      }
    }
    return termPolicyRateClass;
  }

  static convertedFromSanderling(policy: CoverpathBasePolicy): boolean {
    if (!policy.convertedFrom) {
      return false;
    }
    return CoverpathUtils.isSanderlingPolicy(this.getConvertedFromPolicy(policy).product);
  }

  static getConvertedFromPolicy(policy: CoverpathBasePolicy): Policy {
    if (!policy.convertedFrom) {
      return;
    }

    const convertedFromPolicyNumber = policy.convertedFrom;
    const convertedFromApplication = policy.customer.activeApplications.find((application) =>
      application.policies.some((policy) => policy.policyNumber === convertedFromPolicyNumber)
    );

    return convertedFromApplication.policies.find((policy) => policy.policyNumber === convertedFromPolicyNumber);
  }

  static getGenderInPrettyFormat(gender: string | number, state: string) {
    if (this.isUnisexState(Product.channels.CAS.code, state)) {
      return 'Unisex';
    }

    switch (gender) {
      case 1:
      case '1':
      case 'M':
      case 'm':
        return 'Male';
      case 2:
      case '2':
      case 'F':
      case 'f':
        return 'Female';
      default:
        return gender;
    }
  }

  /**
   * This can be removed once PA starts sending loan interest rates for IFIs HL-66990.
   * asOfDate rules: https://havenlife-team.slack.com/archives/G01L293E8UA/p1658847908499379
   *
   * @param effectiveDate Date the policy takes effect
   * @param now The current Date/Time. Optional. Used only for testing.
   * @returns A clone of the effective date with the corrected year.
   */
  static getLoanInterestRateAsOfDate(effectiveDate: Date, now: Date = Utils.now()): Date {
    const result = new Date(effectiveDate);
    const currentYear = now.getFullYear();
    result.setFullYear(currentYear);
    if (result > now) {
      result.setFullYear(currentYear - 1);
    }
    return result;
  }

  static getFloridaLicense(agentLicenseList, producerId) {
    let licenseNumber;
    if (agentLicenseList) {
      const floridaStateLicense = agentLicenseList.find((license) => license.state === 'FL');
      if (floridaStateLicense) {
        licenseNumber = floridaStateLicense.licenseNumber;
      } else {
        throw Error(`No FL license found for producerId: ${producerId}`);
      }
      return licenseNumber;
    }
  }

  static producerIdToBpid(producerId: string) {
    if (producerId && producerId.toLowerCase().indexOf('tpd') !== 0) {
      // If not tpd user, remove the first two characters and put 4 leading 0s as that's
      // what cdm-producers expects
      return `0000${producerId.match(/\d+/)[0]}`;
    } else {
      return producerId;
    }
  }

  static convertFlatExtraAmorphicObjectToString(flatExtras: Array<FlatExtra> | uwFlatExtra[]): string {
    const jsonData = {};
    let numberOfFlatExtra = 1;
    flatExtras.forEach(function (flatExtra) {
      const columnFlatExtraName = 'FlatExtra' + numberOfFlatExtra + '.FlatExtraName';
      const columnFlatExtraAmount = 'FlatExtra' + numberOfFlatExtra + '.FlatExtraAmount';
      const columnFlatExtraPremium = 'FlatExtra' + numberOfFlatExtra + '.FlatExtraPremium';
      jsonData[columnFlatExtraName] = flatExtra?.name;
      jsonData[columnFlatExtraAmount] = flatExtra?.amount;
      jsonData[columnFlatExtraPremium] = flatExtra?.premium;
      numberOfFlatExtra++;
    });
    return JSON.stringify(jsonData);
  }

  static getMaxSignDate(policy) {
    const today = moment(Utils.now());
    const workflowStateUpdate = moment(policy.workflowStateUpdate);
    const maxDaysInAM4 = policy.holdTime
      ? Assumptions.autoCancelPolicyAfterHoldDays
      : Assumptions.autoSendPolicyToCustomerWithoutAmendmentDays;
    const daysLeftInAM4 = maxDaysInAM4 + 1 + today.diff(workflowStateUpdate, 'days');
    return today.add(daysLeftInAM4 + Assumptions.AcceleratedConfirmDays, 'days').toDate();
  }
}
